import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { providerOpsAr } from "./provider-ops-translations";

export type AppLanguage = "en" | "ar";
export type ResolvedLanguage = "en" | "ar";
export type ThemePreference = "dark" | "light";
export type ResolvedTheme = "dark" | "light";
export type TranslationValues = Record<string, number | string | null | undefined>;
export type TranslationFunction = (key: string, values?: TranslationValues) => string;

const en = {
  "common.signOut": "Sign out",
  "preferences.title": "Workspace preferences",
  "preferences.body": "Choose language and theme for this browser.",
  "preferences.language": "Language",
  "preferences.language.system": "Browser default",
  "preferences.language.en": "English",
  "preferences.language.ar": "Arabic",
  "preferences.language.toggleToEn": "Switch to English",
  "preferences.language.toggleToAr": "Switch to Arabic",
  "preferences.theme": "Theme",
  "preferences.theme.system": "System",
  "preferences.theme.dark": "Dark",
  "preferences.theme.light": "Light",
  "preferences.theme.toggleToDark": "Switch to dark mode",
  "preferences.theme.toggleToLight": "Switch to light mode",
  "landing.nav.howItWorks": "Player flow",
  "landing.nav.forVenues": "For venues",
  "landing.nav.platforms": "Platforms",
  "landing.nav.goToWorkspace": "Go to workspace",
  "landing.nav.signIn": "Sign in",
  "landing.nav.register": "Register venue",
  "landing.hero.title.book": "BOOK.",
  "landing.hero.title.checkIn": "CHECK IN.",
  "landing.hero.title.play": "PLAY.",
  "landing.hero.body": "Find gaming venues, request a session and follow your booking in Gaamed. Get the Android app on Google Play or download it on the App Store for iOS.",
  "landing.hero.register": "Run a venue? Register here",
  "landing.hero.cards.bookings.title": "Find venues",
  "landing.hero.cards.bookings.body": "Explore gaming places near you.",
  "landing.hero.cards.sessions.title": "Request a session",
  "landing.hero.cards.sessions.body": "Choose a time and follow your booking.",
  "landing.hero.cards.language.title": "Arabic + English",
  "landing.hero.cards.language.body": "Use the app in the language you prefer.",
  "landing.hero.cards.device.title": "Android + iOS",
  "landing.hero.cards.device.body": "Google Play or App Store.",
  "landing.hero.art.team.body": "Roles stay clear across every shift.",
  "landing.hero.stats.players": "Book in minutes",
  "landing.hero.stats.venues": "Run a calmer shift",
  "landing.hero.stats.language": "Bilingual by default",
  "landing.hero.stats.devices": "Works on any device",
  "landing.how.eyebrow": "Player flow",
  "landing.how.title.line1": "FROM BOOKING.",
  "landing.how.title.line2": "TO ARRIVAL.",
  "landing.how.body": "From choosing a venue to starting your session, here’s how Gaamed works.",
  "landing.how.stepLabel": "Step {number}",
  "landing.how.steps.browse.title": "BROWSE",
  "landing.how.steps.browse.desc": "Open Gaamed, filter by console, PC or VR, pick a nearby venue and check live availability before you commit.",
  "landing.how.steps.book.title": "BOOK",
  "landing.how.steps.book.desc": "Choose a slot and send a booking request. Follow its status in the app while the venue reviews it.",
  "landing.how.steps.checkIn.title": "CHECK IN",
  "landing.how.steps.arrive.title": "ARRIVE",
  "landing.how.steps.checkIn.desc": "Once your booking is confirmed, scan the venue’s check-in QR code when you arrive to start your session.",
  "landing.venues.eyebrow": "For venues",
  "landing.venues.title.line1": "EVERYTHING YOUR",
  "landing.venues.title.line2": "VENUE NEEDS.",
  "landing.venues.body": "Purpose-built for gaming cafes, esports arenas and VR lounges. No generic hospitality software, just the operational stack your venue actually needs.",
  "landing.venues.features.sessions.title": "Live session management",
  "landing.venues.features.sessions.desc": "Start, extend and close sessions in real time. See who is checked in and catch no-shows before they become chaos.",
  "landing.venues.features.calendar.title": "Booking calendar",
  "landing.venues.features.calendar.desc": "Review every upcoming slot across your units from one booking inbox and act on changes without context switching.",
  "landing.venues.features.qr.title": "Fast check-in",
  "landing.venues.features.qr.desc": "When players arrive, staff can confirm and admit them quickly from any browser without slowing the desk down.",
  "landing.venues.features.team.title": "Team access control",
  "landing.venues.features.team.desc": "Invite staff with scoped access so each shift sees exactly what it needs and nothing more.",
  "landing.venues.features.offline.title": "Works offline",
  "landing.venues.features.offline.desc": "The provider portal keeps critical data cached so your team can continue operating during unreliable network moments.",
  "landing.venues.features.policy.title": "Policy controls",
  "landing.venues.features.policy.desc": "Set booking windows, VAT, session limits and no-show rules once, then keep venue operations consistent automatically.",
  "landing.platforms.eyebrow": "Platforms",
  "landing.platforms.title.line1": "FOR VENUES.",
  "landing.platforms.title.line2": "FOR PLAYERS.",
  "landing.platforms.players.eyebrow": "Players",
  "landing.platforms.players.title": "MOBILE APP",
  "landing.platforms.players.desc": "Find the Google Play link and the App Store link for iOS. Need help getting started? The team can follow up about onboarding.",
  "landing.platforms.players.cta": "App links and onboarding",
  "onboarding.home": "Gaamed home",
  "onboarding.back": "Back to Gaamed",
  "onboarding.skip": "Skip to app links and onboarding",
  "onboarding.eyebrow": "For players",
  "onboarding.title": "Get Gaamed.",
  "onboarding.body": "Choose the Android app or the iOS app. For updates or help getting started, leave your email and phone below.",
  "onboarding.install.android": "Get it on Google Play",
  "onboarding.install.ios": "Download on the App Store",
  "onboarding.install.beta": "Beta",
  "onboarding.availability.title": "Available on iOS",
  "onboarding.availability.body": "Get Gaamed for iPhone from the App Store using the link above.",
  "onboarding.form.title": "Get app updates",
  "onboarding.form.body": "App updates are optional. To request them, enter both your email address and an international phone number.",
  "onboarding.form.contact": "Contact details and consent",
  "onboarding.form.email": "Email (required)",
  "onboarding.form.phone": "Phone (required)",
  "onboarding.form.phoneHint": "Start with + and your country code. We won’t guess a country from a local number.",
  "onboarding.form.consent": "I agree that Gaamed may contact me by email or phone about app availability and onboarding.",
  "onboarding.form.submit": "Get app updates",
  "onboarding.form.sending": "Sending…",
  "onboarding.form.privacy": "Read how Gaamed handles your information in the",
  "onboarding.form.dataPolicy": "Only platform admins can access these contact details. We keep them indefinitely unless you request deletion.",
  "onboarding.form.deletion": "To request deletion of these contact details, email",
  "onboarding.privacy.title": "App updates and onboarding contacts",
  "onboarding.privacy.body": "If you request app updates or onboarding follow-up through /join, we collect your email address and international phone number, with your consent, to contact you about app availability and onboarding.",
  "onboarding.privacy.separate": "This contact list is separate from your Gaamed account, booking and payment records. Use the support email below to request deletion of these app-updates contact details, rather than the account-deletion page.",
  "onboarding.error.review": "Please check the highlighted fields before sending.",
  "onboarding.error.emailRequired": "Enter your email address.",
  "onboarding.error.phoneRequired": "Enter your international phone number.",
  "onboarding.error.email": "Enter a valid email address, such as you@example.com.",
  "onboarding.error.phone": "Enter a phone number starting with + and your country code (8–15 digits).",
  "onboarding.error.consent": "Agree to be contacted before sending your request.",
  "onboarding.error.invalid": "Your request wasn’t accepted. Check your contact details and consent, then try again.",
  "onboarding.error.rateLimited": "Too many requests. Your request hasn’t been confirmed. Please wait before trying again.",
  "onboarding.error.unavailable": "Contact requests are temporarily unavailable. Your request hasn’t been confirmed. Please try again later.",
  "onboarding.error.unconfirmed": "We couldn’t confirm your request. Check your connection and try again.",
  "onboarding.retryAfter": "You can try again in {seconds} seconds.",
  "onboarding.success.title": "Your request has been received.",
  "onboarding.success.body": "The Gaamed team may contact you about app availability and onboarding.",
  "onboarding.success.note": "This confirms only your contact request, not an app install or an account registration.",
  "onboarding.share.title": "Share or scan",
  "onboarding.share.body": "Scan to open app links and onboarding on another phone, or share the link below. This QR does not install the app.",
  "onboarding.qrLabel": "QR code to open Gaamed app links and onboarding",
  "landing.platforms.provider.eyebrow": "Venue operators",
  "landing.platforms.provider.title": "VENUE PORTAL",
  "landing.platforms.provider.desc": "The day-to-day workspace for bookings, sessions, player arrivals, staff roles and venue controls.",
  "landing.platforms.provider.cta.open": "Open portal",
  "landing.platforms.provider.cta.signIn": "Sign in to your account",
  "landing.platforms.admin.eyebrow": "Platform",
  "landing.platforms.admin.title": "ADMIN CONSOLE",
  "landing.platforms.admin.desc": "Track platform analytics, review venue approvals, manage providers and keep system operations in view from the admin console.",
  "landing.platforms.admin.cta.open": "Open console",
  "landing.platforms.admin.cta.restricted": "Restricted access",
  "landing.cta.authed.title.line1": "READY TO RUN",
  "landing.cta.authed.title.line2": "YOUR WORKSPACE?",
  "landing.cta.guest.title.line1": "READY TO LIST",
  "landing.cta.guest.title.line2": "YOUR VENUE?",
  "landing.cta.authed.body": "Jump back into your Gaamed workspace to manage bookings, sessions and policy controls.",
  "landing.cta.guest.body": "Join Gaamed today and set up your venue in minutes without dragging your team through a heavy rollout.",
  "landing.cta.register": "Register now",
  "landing.footer.tagline": "The gaming-venue management platform. Book. Check in. Play.",
  "landing.footer.rights": "All rights reserved.",
  "landing.footer.product": "Product",
  "landing.footer.account": "Account",
  "landing.footer.legal": "Legal",
  "landing.footer.terms": "Terms of Service",
  "landing.footer.privacy": "Privacy Policy",
  "landing.footer.refund": "Refund Policy",
  "provider.sections.dashboard": "Home",
  "provider.sections.floor": "Floor",
  "extensions.title": "Pending extension requests",
  "extensions.body": "Review the requested time and quoted price. Pending requests do not change the session end or reserve extra time.",
  "extensions.deadlineHint": "Quotes expire two minutes after creation. Waiting or refreshing does not extend the approval deadline.",
  "extensions.notification": "Extension approval requested",
  "extensions.pendingHint": "Extension requested, not approved. Open the Floor to review it.",
  "extensions.membershipRequired": "Owner or manager membership is required to review these requests.",
  "extensions.offline": "You’re offline. Requests shown were last checked online. Approve, reject and refresh are unavailable until you reconnect.",
  "extensions.refresh": "Refresh",
  "extensions.refreshRequest": "Refresh request",
  "extensions.loading": "Loading extension requests…",
  "extensions.updating": "Checking current state…",
  "extensions.empty": "No pending extension requests for this venue.",
  "extensions.session": "Session",
  "extensions.currentEnd": "Current session end",
  "extensions.requestedEndPending": "Requested end (awaiting confirmation)",
  "extensions.requestedEnd": "Requested end",
  "extensions.additionalTime": "Requested extra time",
  "extensions.minutes": "+{minutes} min",
  "extensions.additionalAmount": "Quoted extra-time amount",
  "extensions.currentTotal": "Current quoted time total",
  "extensions.proposedTotal": "Quoted time total if approved",
  "extensions.deadline": "Approval deadline",
  "extensions.timezone": "Times shown in {timezone}.",
  "extensions.noPlannedEnd": "No planned end",
  "extensions.notAvailable": "Unavailable",
  "extensions.targetHint": "An extension request for this session needs review.",
  "extensions.approve": "Approve",
  "extensions.reject": "Reject",
  "extensions.approving": "Approving…",
  "extensions.rejecting": "Rejecting…",
  "extensions.unconfirmed": "Decision not confirmed",
  "extensions.deadlinePassed": "Deadline passed",
  "extensions.expiredHint": "The deadline has passed on this device. Refresh to check the server’s current result.",
  "extensions.changedHint": "The session or quote details changed or are incomplete. Refresh before deciding.",
  "extensions.refreshBeforeDecision": "Refresh this request before another decision. The previous attempt may already have been applied.",
  "extensions.phase.pending_approval": "Awaiting approval",
  "extensions.phase.preview": "Not submitted",
  "extensions.phase.applied": "Approved",
  "extensions.phase.rejected": "Rejected",
  "extensions.phase.cancelled": "Cancelled by customer",
  "extensions.phase.expired": "Expired",
  "extensions.phase.stale": "No longer valid",
  "extensions.notice.applied": "Extension approved by the server.",
  "extensions.notice.rejected": "Extension rejected. This request did not change the session end.",
  "extensions.error.denied": "You no longer have access to review these requests. Refresh your workspace or sign in again.",
  "extensions.error.load": "Could not load current extension requests. Refresh to try again.",
  "extensions.error.unavailable": "This request is no longer available or you do not have access to it.",
  "extensions.error.unconfirmed": "We couldn’t confirm the decision. Checking the current request before any retry.",
  "extensions.error.recovery": "We couldn’t check the decision. Refresh this request before trying again; it may already have been applied.",
  "extensions.error.pendingRetry": "The server still shows this request as pending. Review its details before trying the same decision again.",
  "extensions.error.expired": "The approval deadline passed. This attempt did not approve any new time.",
  "extensions.error.priceChanged": "The quoted price is no longer current. The customer needs to review a new quote.",
  "extensions.error.conflict": "The requested time conflicts with a booking or venue closure. Review the current request and schedule.",
  "extensions.error.sessionChanged": "The session ended or is no longer available for this extension. Review its current state.",
  "extensions.error.stale": "The session or request changed, or a decision was already made. Review its current state.",
  "extensions.error.cancelled": "The customer cancelled this request. No approval is needed.",
  "extensions.error.floorRefresh": "Approval is confirmed, but the Floor could not be refreshed. Refresh again while online.",
  "provider.sections.orders": "Orders",
  "provider.sections.schedule": "Bookings",
  "provider.sections.register": "Checkout",
  "provider.sections.customers": "Customers",
  "provider.sections.venueSetup": "Venue setup",
  "setup.units.resourceHint": "Create one unit for each physical room, station, table or court that can be booked on its own. Don’t add separate units for activities in the same room.",
  "setup.units.typeHint": "Choose {roomType} for a whole room. For a separately bookable station, table or court, use the matching type, or {otherType} if none fits.",
  "setup.units.roomGamesHint": "For games available in this room, save it first, then close this form and open Games in Venue setup to assign them to this room.",
  "provider.sections.tournaments": "Tournaments",
  "provider.sections.compact.tournaments": "Events",
  "provider.sections.menu": "Menu & stock",
  "provider.sections.business": "Business & team",
  "provider.sections.compact.dashboard": "Home",
  "provider.sections.compact.floor": "Floor",
  "provider.sections.compact.orders": "Orders",
  "provider.sections.compact.schedule": "Bookings",
  "provider.sections.compact.register": "Checkout",
  "provider.sections.compact.customers": "Customers",
  "provider.sections.compact.menu": "Menu",
  "provider.sections.compact.venueSetup": "Venue",
  "provider.sections.compact.business": "Business",
  "provider.sectionsMenu": "Operations",
  "provider.setupMenu": "Setup",
  "provider.quickActions": "Quick actions",
  "provider.createVenue": "Create venue",
  "provider.newBooking": "New booking",
  "provider.startWalkIn": "Start walk-in",
  "Confirmation": "Confirmation",
  "Discard changes": "Discard changes",
  "Discard unsaved changes?": "Discard unsaved changes?",
  "You have unsaved changes. Switching tabs will discard them.": "You have unsaved changes. Switching tabs will discard them.",
  "More": "More",
  "settings.policy.eyebrow": "Policy settings",
  "settings.policy.title": "Venue operations policy",
  "settings.policy.body": "These settings apply across all your venues as provider-level defaults. You can refine them per venue in a future update.",
  "settings.save": "Save policy",
  "settings.saving": "Saving...",
  "Status": "Status",
  "issued": "Issued",
  "voided": "Voided",
  "Receipt {number} voided.": "Receipt {number} voided.",
  "Could not void receipt.": "Could not void receipt.",
  "Enter a reason before voiding this receipt.": "Enter a reason before voiding this receipt.",
  "Voided: {reason}": "Voided: {reason}",
  "No reason recorded": "No reason recorded",
  "Void receipt": "Void receipt",
  "Refund required": "Refund required",
  "Receipts with payments need refund handling before they can be voided.": "Receipts with payments need refund handling before they can be voided.",
  "Reason": "Reason",
  "Mistake, duplicate, customer left...": "Mistake, duplicate, customer left...",
  "Voiding...": "Voiding...",
  "Issue receipt": "Issue receipt",
  "Issuing...": "Issuing...",
  "Receipt {number} issued.": "Receipt {number} issued.",
  "Receipt {number} issued and paid.": "Receipt {number} issued and paid.",
  "Live view": "Live view",
  "{count} total units": "{count} total units",
  "All": "All",
  "Available": "Available",
  "Occupied": "Occupied",
  "Reserved": "Reserved",
  "Blocked": "Blocked",
  "Offline": "Offline",
  "All types": "All types",
  "Search units": "Search units",
  "Search units or guests...": "Search units or guests...",
  "Duration": "Duration",
  "Note": "Note",
  "Guest contact": "Guest contact",
  "{count} seats": "{count} seats",
  "VIP": "VIP",
  "Ready for walk-in": "Ready for walk-in",
  "{duration} planned · {amount} est.": "{duration} planned · {amount} est.",
  "No live controls while this unit is unavailable.": "No live controls while this unit is unavailable.",
  "Ends {value}": "Ends {value}",
  "Elapsed": "Elapsed",
  "Remaining": "Remaining",
  "Overtime": "Overtime",
  "Checkout": "Checkout",
  "Food": "Food",
  "End options": "End options",
  "End and open checkout": "End and open checkout",
  "Payment was already collected at the desk": "Payment was already collected at the desk",
  "Walk-in duration must be at least 15 minutes.": "Walk-in duration must be at least 15 minutes.",
  "Walk-in duration cannot exceed 720 minutes.": "Walk-in duration cannot exceed 720 minutes.",
  "Open receipt": "Open receipt",
  "Open checkout": "Open checkout",
  "Ended {value}": "Ended {value}",
  "No units match the current filters.": "No units match the current filters.",
  "Qty": "Qty",
  "{quantity} x {name} · {price} each": "{quantity} x {name} · {price} each",
  "admin.dashboard.providers.title": "Providers",
  "admin.dashboard.providers.pending": "Pending registrations",
  "admin.dashboard.providers.approved": "Approved providers",
  "admin.dashboard.providers.suspended": "Suspended providers",
  "admin.dashboard.venues.title": "Venues",
  "admin.dashboard.venues.pendingApprovals": "Pending venue approvals",
  "admin.dashboard.venues.removalRequests": "Pending removal requests",
  "admin.dashboard.venues.approved": "Approved venues",
  "admin.dashboard.activity.title": "Activity",
  "admin.dashboard.activity.todayBookings": "Bookings today",
  "admin.dashboard.activity.activeSessions": "Active sessions",
  "admin.dashboard.activity.totalBookings": "Total bookings",
  "admin.dashboard.recentActivity": "Recent admin activity",
  "admin.dashboard.noRecentActivity": "No admin activity recorded yet.",
  "admin.reviews.eyebrow": "Review moderation",
  "admin.reviews.title": "Pending reviews",
  "admin.reviews.empty": "No pending reviews to moderate.",
  "admin.reviews.loading": "Loading reviews...",
  "admin.reviews.error": "Unable to load pending reviews.",
  "admin.reviews.approve": "Approve",
  "admin.reviews.reject": "Reject",
  "admin.reviews.processing": "Processing...",
  "admin.reviews.reviewNote": "Review note",
  "admin.reviews.rejectionNoteRequired": "A review note is required to reject a review.",
  "admin.reviews.approvedNotice": "Review approved.",
  "admin.reviews.rejectedNotice": "Review rejected.",
  "admin.reviews.submitted": "Submitted",
  "admin.reviews.comment": "Comment",
  "admin.reviews.noComment": "No comment.",
  "admin.reports.eyebrow": "UGC safeguards",
  "admin.reports.title": "Report queue",
  "admin.reports.empty": "No pending reports.",
  "admin.reports.dismiss": "Dismiss",
  "admin.reports.remove": "Remove review",
  "admin.reports.restrict": "Restrict author",
  "admin.reports.resolved": "Report resolved.",
  "admin.reports.error": "Unable to resolve report.",
  "admin.reports.venue": "Venue",
  "admin.reports.stars": "Stars",
  "admin.reports.comment": "Evidence comment",
  "admin.reports.reviewChanged": "Review last changed",
  "admin.reports.rationalePlaceholder": "Rationale required for this decision...",
  "admin.reports.currentState": "Current review state",
  "admin.reports.stale": "stale snapshot; removal disabled",
  "admin.reports.currentReview": "Current review",
  "admin.reports.noCurrentReview": "No current review",
  "Start / End Day": "Start / End Day",
  "Info": "Info",
  "Food / Drink": "Food / Drink",
  "Menu management is locked until the venue is approved.": "Menu management is locked until the venue is approved.",
  "Manage units from the venue list on the left, or use the unit cards below.": "Manage units from the venue list on the left, or use the unit cards below.",
  "Team": "Team",
  "Business tabs": "Business tabs",
  "Start Day": "Start Day",
  "End Day": "End Day",
  "Start Day & Open Drawer": "Start Day & Open Drawer",
  "End Day & Close Drawer": "End Day & Close Drawer",
  "Day started. Cash drawer is open.": "Day started. Cash drawer is open.",
  "Day ended. Cash drawer is closed.": "Day ended. Cash drawer is closed.",
  "Starting day...": "Starting day...",
  "Ending day...": "Ending day...",
  "Could not start day.": "Could not start day.",
  "Could not end day.": "Could not end day.",
  "You have unsaved changes. Leaving this page will discard them.": "You have unsaved changes. Leaving this page will discard them.",
  "Select a venue first.": "Select a venue first.",
  "Close": "Close",
  "Incoming bookings": "Incoming bookings",
  "Loading...": "Loading...",
  "No upcoming bookings.": "No upcoming bookings.",
  "Copy check-in QR": "Copy check-in QR",
  "Pending confirmations": "Pending confirmations",
  "{count} booking awaiting confirmation": "{count} booking awaiting confirmation",
  "{count} bookings awaiting confirmation": "{count} bookings awaiting confirmation",
  "Customers can't check in until these bookings are confirmed.": "Customers can't check in until these bookings are confirmed.",
  "Review": "Review",
  "Dismiss pending booking alert": "Dismiss pending booking alert",
  "{count} pending": "{count} pending",
  "Notifications": "Notifications",
  "{count} unread": "{count} unread",
  "Mark all read": "Mark all read",
  "Mark as read": "Mark as read",
  "No notifications yet.": "No notifications yet.",
  "New bookings, sessions and food orders land here in real time.": "New bookings, sessions and food orders land here in real time.",
  "Just now": "Just now",
  "{count} min ago": "{count} min ago",
  "{count}h ago": "{count}h ago",
  "{count}d ago": "{count}d ago",
  "Open notification": "Open notification",
  "New booking": "New booking",
  "New booking from {name}": "New booking from {name}",
  "Booking updated": "Booking updated",
  "Booking updated for {name}": "Booking updated for {name}",
  "Session started": "Session started",
  "Session started for {name}": "Session started for {name}",
  "Session updated": "Session updated",
  "Session updated for {name}": "Session updated for {name}",
  "Session ended": "Session ended",
  "Session ended for {name}": "Session ended for {name}",
  "New food order": "New food order",
  "Food order updated": "Food order updated",
} as const;

const ar: Record<string, string> = {
  // Spread first so an established translation below always wins.
  ...providerOpsAr,
  "Your sign-in has expired. Sign in again to open this notification.": "انتهت جلسة تسجيل الدخول. سجّل الدخول مجددًا لفتح هذا الإشعار.",
  "Could not load this notification's record. Check your connection and open the notification again.": "تعذر تحميل تفاصيل هذا الإشعار. تحقق من اتصالك وافتح الإشعار مرة أخرى.",
  "This notification's record is no longer available or you do not have access to it.": "لم تعد تفاصيل هذا الإشعار متاحة، أو ليس لديك صلاحية للوصول إليها.",
  "Back to floor": "العودة إلى الأجهزة",
  "Games": "الألعاب",
  "Venue logo": "شعار المكان",
  "Upload logo": "رفع شعار",
  "Replace logo": "تغيير الشعار",
  "{name} logo": "شعار {name}",
  "Shown as the venue's branded mark where a logo is supported.": "يظهر هذا الشعار لتعريف اللاعبين بالمكان في المواضع التي تدعم عرض الشعارات.",
  "No logo yet. The app shows your main photo instead.": "لم تُضف شعارًا بعد. تظهر الصورة الرئيسية للمكان بدلًا منه.",
  "common.signOut": "تسجيل الخروج",
  "preferences.title": "تفضيلات مساحة العمل",
  "preferences.body": "اختر اللغة والمظهر لهذا المتصفح.",
  "preferences.language": "اللغة",
  "preferences.language.system": "حسب المتصفح",
  "preferences.language.en": "الإنجليزية",
  "preferences.language.ar": "العربية",
  "preferences.language.toggleToEn": "التبديل إلى الإنجليزية",
  "preferences.language.toggleToAr": "التبديل إلى العربية",
  "preferences.theme": "المظهر",
  "preferences.theme.system": "حسب النظام",
  "preferences.theme.dark": "داكن",
  "preferences.theme.light": "فاتح",
  "preferences.theme.toggleToDark": "التبديل إلى الوضع الداكن",
  "preferences.theme.toggleToLight": "التبديل إلى الوضع الفاتح",
  "landing.nav.howItWorks": "رحلة اللاعب",
  "landing.nav.forVenues": "للأماكن",
  "landing.nav.platforms": "المنصات",
  "landing.nav.goToWorkspace": "افتح مساحة العمل",
  "landing.nav.signIn": "تسجيل الدخول",
  "landing.nav.register": "سجّل مكانك",
  "landing.hero.title.book": "احجز.",
  "landing.hero.title.checkIn": "سجّل دخولك.",
  "landing.hero.title.play": "العب.",
  "landing.hero.body": "اكتشف أماكن الجيمنج واطلب جلسة وتابع حجزك على Gaamed. حمّل تطبيق Android من Google Play أو حمّله على iOS من App Store.",
  "landing.hero.register": "عندك مكان؟ سجّله هنا",
  "landing.hero.cards.bookings.title": "اكتشف الأماكن",
  "landing.hero.cards.bookings.body": "شوف أماكن الجيمنج القريبة منك.",
  "landing.hero.cards.sessions.title": "اطلب جلسة",
  "landing.hero.cards.sessions.body": "اختار الوقت وتابع حجزك.",
  "landing.hero.cards.language.title": "عربي + إنجليزي",
  "landing.hero.cards.language.body": "استخدم التطبيق باللغة اللي تفضلها.",
  "landing.hero.cards.device.title": "Android و iOS",
  "landing.hero.cards.device.body": "Google Play أو App Store.",
  "landing.hero.art.team.body": "الصلاحيات تبقى واضحة في كل شِفت.",
  "landing.hero.stats.players": "يحجزون خلال دقائق",
  "landing.hero.stats.venues": "وردية تشغيل أهدأ",
  "landing.hero.stats.language": "ثنائي اللغة افتراضياً",
  "landing.hero.stats.devices": "يعمل على أي جهاز",
  "landing.how.eyebrow": "رحلة اللاعب",
  "landing.how.title.line1": "من الحجز.",
  "landing.how.title.line2": "لحد الوصول.",
  "landing.how.body": "من اختيار المكان لحد ما تبدأ جلستك، دي طريقة استخدام Gaamed.",
  "landing.how.stepLabel": "الخطوة {number}",
  "landing.how.steps.browse.title": "استكشف",
  "landing.how.steps.browse.desc": "افتح Gaamed، صفِّ حسب الكونسول أو الحاسب أو الواقع الافتراضي، اختر مكاناً قريباً وتحقق من الإتاحة المباشرة قبل التأكيد.",
  "landing.how.steps.book.title": "احجز",
  "landing.how.steps.book.desc": "اختار الموعد وابعت طلب الحجز. تابع حالته في التطبيق لحد ما المكان يراجعه.",
  "landing.how.steps.arrive.title": "احضر",
  "landing.how.steps.checkIn.desc": "بعد تأكيد حجزك، امسح كود تسجيل الوصول الموجود في المكان عشان تبدأ جلستك.",
  "landing.venues.eyebrow": "للأماكن",
  "landing.venues.title.line1": "كل ما يحتاجه",
  "landing.venues.title.line2": "مكانك.",
  "landing.venues.body": "مصممة خصيصاً لمقاهي الألعاب وساحات الرياضات الإلكترونية وصالات الواقع الافتراضي. ليست منصة ضيافة عامة، بل طبقة تشغيل تناسب نوع مكانك فعلاً.",
  "landing.venues.features.sessions.title": "إدارة الجلسات المباشرة",
  "landing.venues.features.sessions.desc": "ابدأ الجلسات ومددها وأنهِها لحظياً. اعرف من سجّل حضوره والتقط حالات عدم الحضور قبل أن تتحول إلى فوضى.",
  "landing.venues.features.calendar.title": "تقويم الحجوزات",
  "landing.venues.features.calendar.desc": "راجع كل المواعيد القادمة عبر وحداتك من صندوق حجز واحد وتعامل مع التغييرات من دون التنقل بين شاشات متعددة.",
  "landing.venues.features.qr.title": "دخول سريع",
  "landing.venues.features.qr.desc": "عند وصول اللاعبين يستطيع الفريق تأكيدهم وإدخالهم بسرعة من أي متصفح من دون تعطيل مكتب الاستقبال.",
  "landing.venues.features.team.title": "تحكم بصلاحيات الفريق",
  "landing.venues.features.team.desc": "ادعُ الموظفين بصلاحيات محددة حتى ترى كل مناوبة ما تحتاجه فقط بلا وصول زائد.",
  "landing.venues.features.offline.title": "يعمل دون اتصال",
  "landing.venues.features.offline.desc": "تحفظ بوابة المزود البيانات الحرجة محلياً حتى يواصل فريقك العمل أثناء ضعف الشبكة أو انقطاعها.",
  "landing.venues.features.policy.title": "ضبط السياسات",
  "landing.venues.features.policy.desc": "حدد نافذة الحجز والضريبة وحدود الجلسات وسياسات عدم الحضور مرة واحدة، ثم اترك المنصة تطبقها باستمرار.",
  "landing.platforms.eyebrow": "المنصات",
  "landing.platforms.title.line1": "لأماكن الألعاب.",
  "landing.platforms.title.line2": "وللاعبين.",
  "landing.platforms.players.eyebrow": "اللاعبون",
  "landing.platforms.players.title": "تطبيق الجوال",
  "landing.platforms.players.desc": "افتح رابط Google Play أو رابط App Store للـ iOS. لو محتاج مساعدة تبدأ، الفريق ممكن يتواصل معاك.",
  "landing.platforms.players.cta": "روابط التطبيق والمساعدة",
  "onboarding.home": "صفحة Gaamed الرئيسية",
  "onboarding.back": "الرجوع إلى Gaamed",
  "onboarding.skip": "انتقل لروابط التطبيق والمساعدة",
  "onboarding.eyebrow": "للاعبين",
  "onboarding.title": "ابدأ مع Gaamed.",
  "onboarding.body": "اختار تطبيق Android أو تطبيق iOS. لو عايز أخبار التطبيق أو مساعدة تبدأ، سيب بريدك الإلكتروني ورقم تليفونك تحت.",
  "onboarding.install.android": "حمّل من Google Play",
  "onboarding.install.ios": "حمّله من App Store",
  "onboarding.install.beta": "تجريبي",
  "onboarding.availability.title": "متاح على iOS",
  "onboarding.availability.body": "حمّل Gaamed للـ iPhone من App Store بالرابط اللي فوق.",
  "onboarding.form.title": "تابع أخبار التطبيق",
  "onboarding.form.body": "متابعة أخبار التطبيق اختيارية. لو عايزها، اكتب بريدك الإلكتروني ورقم تليفونك بالصيغة الدولية؛ الاتنين مطلوبين.",
  "onboarding.form.contact": "بيانات التواصل والموافقة",
  "onboarding.form.email": "البريد الإلكتروني (مطلوب)",
  "onboarding.form.phone": "رقم التليفون (مطلوب)",
  "onboarding.form.phoneHint": "ابدأ بعلامة + وكود بلدك. مش هنفترض بلد من رقم محلي.",
  "onboarding.form.consent": "أوافق إن Gaamed يتواصل معايا بالبريد الإلكتروني أو التليفون بخصوص توفر التطبيق ومساعدتي أبدأ استخدامه.",
  "onboarding.form.submit": "تابع أخبار التطبيق",
  "onboarding.form.sending": "جاري الإرسال…",
  "onboarding.form.privacy": "اعرف إزاي Gaamed بيتعامل مع بياناتك من",
  "onboarding.form.dataPolicy": "مسؤولو المنصة بس يقدروا يشوفوا بيانات التواصل دي. بنحتفظ بيها لمدة غير محددة إلا لو طلبت حذفها.",
  "onboarding.form.deletion": "عشان تطلب حذف بيانات التواصل دي، ابعت إيميل إلى",
  "onboarding.privacy.title": "بيانات التواصل لأخبار التطبيق والمساعدة",
  "onboarding.privacy.body": "لو طلبت أخبار التطبيق أو مساعدة تبدأ من صفحة /join، بنجمع بريدك الإلكتروني ورقم تليفونك بالصيغة الدولية، بموافقتك، عشان نتواصل معاك بخصوص توفر التطبيق ومساعدتك تبدأ استخدامه.",
  "onboarding.privacy.separate": "قائمة التواصل دي منفصلة عن حساب Gaamed وبيانات الحجوزات والمدفوعات. اطلب حذف بيانات التواصل لأخبار التطبيق من إيميل الدعم اللي تحت، مش من صفحة حذف الحساب.",
  "onboarding.error.review": "راجع الخانات المحددة قبل الإرسال.",
  "onboarding.error.emailRequired": "اكتب بريدك الإلكتروني.",
  "onboarding.error.phoneRequired": "اكتب رقم تليفونك بالصيغة الدولية.",
  "onboarding.error.email": "اكتب بريد إلكتروني صحيح، زي you@example.com.",
  "onboarding.error.phone": "اكتب رقم تليفون بيبدأ بعلامة + وكود بلدك (من 8 إلى 15 رقم).",
  "onboarding.error.consent": "وافق على التواصل معاك قبل إرسال طلبك.",
  "onboarding.error.invalid": "طلبك ما اتقبلش. راجع بيانات التواصل والموافقة وجرب تاني.",
  "onboarding.error.rateLimited": "في طلبات كتير. طلبك لسه مش مؤكد. استنى قبل ما تجرب تاني.",
  "onboarding.error.unavailable": "طلبات التواصل مش متاحة مؤقتًا. طلبك لسه مش مؤكد. جرب تاني بعدين.",
  "onboarding.error.unconfirmed": "ما قدرناش نأكد طلبك. اتأكد من اتصالك بالإنترنت وجرب تاني.",
  "onboarding.retryAfter": "تقدر تجرب تاني بعد {seconds} ثانية.",
  "onboarding.success.title": "طلبك وصلنا.",
  "onboarding.success.body": "فريق Gaamed ممكن يتواصل معاك بخصوص توفر التطبيق ومساعدتك تبدأ استخدامه.",
  "onboarding.success.note": "ده تأكيد لطلب التواصل بس، مش تثبيت للتطبيق ولا تسجيل حساب.",
  "onboarding.share.title": "شارك الرابط أو امسح الكود",
  "onboarding.share.body": "امسح الكود عشان تفتح روابط التطبيق والمساعدة على تليفون تاني، أو شارك الرابط اللي تحت. الكود ده مش بيثبت التطبيق.",
  "onboarding.qrLabel": "كود QR لفتح روابط تطبيق Gaamed والمساعدة",
  "landing.platforms.provider.eyebrow": "مشغلو الأماكن",
  "landing.platforms.provider.title": "بوابة المكان",
  "landing.platforms.provider.desc": "دي شاشة الشغل اليومية للحجوزات والجلسات ودخول اللاعبين وصلاحيات الفريق وتحكم المكان.",
  "landing.platforms.provider.cta.open": "افتح البوابة",
  "landing.platforms.provider.cta.signIn": "سجّل الدخول إلى حسابك",
  "landing.platforms.admin.eyebrow": "المنصة",
  "landing.platforms.admin.title": "وحدة الإدارة",
  "landing.platforms.admin.desc": "تابع تحليلات المنصة ووافق على الأماكن وأدر المزودين وراقب التشغيل العام من وحدة الإدارة.",
  "landing.platforms.admin.cta.open": "افتح اللوحة",
  "landing.platforms.admin.cta.restricted": "وصول مقيّد",
  "landing.cta.authed.title.line1": "جاهز لإدارة",
  "landing.cta.authed.title.line2": "مساحة عملك؟",
  "landing.cta.guest.title.line1": "جاهز لإدراج",
  "landing.cta.guest.title.line2": "مكانك؟",
  "landing.cta.authed.body": "ارجع إلى مساحة عمل Gaamed لإدارة الحجوزات والجلسات والسياسات.",
  "landing.cta.guest.body": "انضم إلى Gaamed اليوم وجهّز مكانك خلال دقائق من دون إدخال فريقك في إطلاق ثقيل أو معقد.",
  "landing.cta.register": "سجّل الآن",
  "landing.footer.tagline": "منصة إدارة أماكن الألعاب. احجز. سجّل حضورك. العب.",
  "landing.footer.rights": "جميع الحقوق محفوظة.",
  "landing.footer.product": "المنتج",
  "landing.footer.account": "الحساب",
  "landing.footer.legal": "القانوني",
  "landing.footer.terms": "شروط الخدمة",
  "landing.footer.privacy": "سياسة الخصوصية",
  "landing.footer.refund": "سياسة الاسترداد",
  "provider.sections.dashboard": "الرئيسية",
  "provider.sections.floor": "الصالة",
  "extensions.title": "طلبات تمديد منتظرة الموافقة",
  "extensions.body": "راجع الوقت المطلوب والسعر المعروض. الطلب المنتظر مش بيغيّر نهاية الجلسة ولا بيحجز وقت إضافي.",
  "extensions.deadlineHint": "عرض السعر بينتهي بعد دقيقتين من إنشائه. الانتظار أو التحديث مش بيزوّد مهلة الموافقة.",
  "extensions.notification": "طلب موافقة على تمديد جلسة",
  "extensions.pendingHint": "ده طلب تمديد، مش موافقة. افتح الصالة عشان تراجعه.",
  "extensions.membershipRequired": "لازم تكون مالك أو مدير في عضوية المكان عشان تراجع الطلبات دي.",
  "extensions.offline": "إنت مش متصل بالإنترنت. الطلبات المعروضة اتراجعت آخر مرة أثناء الاتصال. الموافقة والرفض والتحديث مش متاحين لحد ما ترجع تتصل.",
  "extensions.refresh": "تحديث",
  "extensions.refreshRequest": "تحديث الطلب",
  "extensions.loading": "جاري تحميل طلبات التمديد…",
  "extensions.updating": "جاري مراجعة الحالة الحالية…",
  "extensions.empty": "مفيش طلبات تمديد منتظرة الموافقة للمكان ده.",
  "extensions.session": "الجلسة",
  "extensions.currentEnd": "نهاية الجلسة الحالية",
  "extensions.requestedEndPending": "النهاية المطلوبة (منتظرة التأكيد)",
  "extensions.requestedEnd": "النهاية المطلوبة",
  "extensions.additionalTime": "الوقت الإضافي المطلوب",
  "extensions.minutes": "+{minutes} دقيقة",
  "extensions.additionalAmount": "المبلغ المعروض للوقت الإضافي",
  "extensions.currentTotal": "إجمالي الوقت في العرض الحالي",
  "extensions.proposedTotal": "إجمالي الوقت المعروض لو تمت الموافقة",
  "extensions.deadline": "آخر موعد للموافقة",
  "extensions.timezone": "الأوقات بتوقيت {timezone}.",
  "extensions.noPlannedEnd": "مفيش نهاية محددة",
  "extensions.notAvailable": "غير متاح",
  "extensions.targetHint": "في طلب تمديد للجلسة دي محتاج مراجعة.",
  "extensions.approve": "موافقة",
  "extensions.reject": "رفض",
  "extensions.approving": "جاري الموافقة…",
  "extensions.rejecting": "جاري الرفض…",
  "extensions.unconfirmed": "القرار لسه مش مؤكد",
  "extensions.deadlinePassed": "المهلة انتهت",
  "extensions.expiredHint": "المهلة انتهت حسب وقت الجهاز. حدّث الطلب عشان تراجع النتيجة الحالية على السيرفر.",
  "extensions.changedHint": "بيانات الجلسة أو عرض السعر اتغيرت أو مش كاملة. حدّث الطلب قبل القرار.",
  "extensions.refreshBeforeDecision": "حدّث الطلب قبل قرار تاني. المحاولة السابقة ممكن تكون اتنفذت بالفعل.",
  "extensions.phase.pending_approval": "منتظر الموافقة",
  "extensions.phase.preview": "لسه ما اتبعتش",
  "extensions.phase.applied": "تمت الموافقة",
  "extensions.phase.rejected": "مرفوض",
  "extensions.phase.cancelled": "العميل لغى الطلب",
  "extensions.phase.expired": "منتهي",
  "extensions.phase.stale": "لم يعد صالحًا",
  "extensions.notice.applied": "السيرفر أكد الموافقة على التمديد.",
  "extensions.notice.rejected": "طلب التمديد اترفض. الطلب ده ما غيّرش نهاية الجلسة.",
  "extensions.error.denied": "ما بقاش عندك صلاحية تراجع الطلبات دي. حدّث مساحة العمل أو سجّل الدخول تاني.",
  "extensions.error.load": "ما قدرناش نحمل طلبات التمديد الحالية. حدّث عشان تجرب تاني.",
  "extensions.error.unavailable": "الطلب ده مش متاح دلوقتي أو ما عندكش صلاحية تشوفه.",
  "extensions.error.unconfirmed": "ما قدرناش نأكد القرار. بنراجع الطلب الحالي قبل أي إعادة محاولة.",
  "extensions.error.recovery": "ما قدرناش نراجع القرار. حدّث الطلب قبل ما تجرب تاني؛ ممكن يكون اتنفذ بالفعل.",
  "extensions.error.pendingRetry": "الطلب لسه منتظر على السيرفر. راجع بياناته قبل ما تحاول نفس القرار تاني.",
  "extensions.error.expired": "مهلة الموافقة انتهت. المحاولة دي ما وافقتش على وقت جديد.",
  "extensions.error.priceChanged": "السعر المعروض ما بقاش حالي. العميل محتاج يراجع عرض جديد.",
  "extensions.error.conflict": "الوقت المطلوب بيتعارض مع حجز أو إغلاق للمكان. راجع الطلب الحالي والمواعيد.",
  "extensions.error.sessionChanged": "الجلسة انتهت أو ما بقتش متاحة للتمديد ده. راجع حالتها الحالية.",
  "extensions.error.stale": "الجلسة أو الطلب اتغير، أو في قرار اتاخد بالفعل. راجع الحالة الحالية.",
  "extensions.error.cancelled": "العميل لغى الطلب. مش محتاج موافقة.",
  "extensions.error.floorRefresh": "الموافقة مؤكدة، لكن ما قدرناش نحدّث الصالة. حدّث تاني وإنت متصل.",
  "provider.sections.orders": "الطلبات",
  "provider.sections.schedule": "الحجوزات",
  "provider.sections.register": "الحساب والدفع",
  "provider.sections.customers": "العملاء",
  "provider.sections.venueSetup": "إعداد المكان",
  "setup.units.resourceHint": "ضيف وحدة واحدة لكل غرفة أو جهاز أو ترابيزة أو ملعب بيتحجز لوحده. ما تضيفش وحدات منفصلة لأنشطة مختلفة في نفس الغرفة.",
  "setup.units.typeHint": "اختار {roomType} لو الحجز للغرفة كلها. للجهاز أو الترابيزة أو الملعب اللي بيتحجز لوحده، اختار النوع المناسب، أو {otherType} لو مفيش نوع مناسب.",
  "setup.units.roomGamesHint": "للألعاب المتاحة في الغرفة دي، احفظها الأول، وبعدها اقفل النموذج وافتح الألعاب في إعداد المكان عشان تربطها بالغرفة دي.",
  "provider.sections.tournaments": "البطولات",
  "provider.sections.compact.tournaments": "بطولات",
  "provider.sections.menu": "المنيو والمخزون",
  "provider.sections.business": "النشاط والفريق",
  "provider.sections.compact.dashboard": "الرئيسية",
  "provider.sections.compact.floor": "الصالة",
  "provider.sections.compact.orders": "الطلبات",
  "provider.sections.compact.schedule": "الحجوزات",
  "provider.sections.compact.register": "الدفع",
  "provider.sections.compact.customers": "العملاء",
  "provider.sections.compact.menu": "المنيو",
  "provider.sections.compact.venueSetup": "المكان",
  "provider.sections.compact.business": "النشاط",
  "provider.sectionsMenu": "العمليات",
  "provider.setupMenu": "الإعداد",
  "provider.quickActions": "إجراءات سريعة",
  "provider.createVenue": "إنشاء مكان",
  "provider.newBooking": "حجز جديد",
  "provider.startWalkIn": "بدء زيارة مباشرة",
  "Confirmation": "تأكيد",
  "Discard changes": "تجاهل التغييرات",
  "Discard unsaved changes?": "تجاهل التغييرات غير المحفوظة؟",
  "You have unsaved changes. Switching tabs will discard them.": "لديك تغييرات غير محفوظة. تبديل علامة التبويب سيتجاهلها.",
  "More": "المزيد",
  "settings.policy.eyebrow": "إعدادات السياسات",
  "settings.policy.title": "سياسة تشغيل الأماكن",
  "settings.policy.body": "تُطبّق هذه الإعدادات كافتراضات على جميع أماكنك. يمكن تخصيصها لكل مكان في تحديث لاحق.",
  "settings.save": "حفظ السياسة",
  "settings.saving": "جار الحفظ...",
  "issued": "صادر",
  "voided": "ملغي",
  "Receipt {number} voided.": "تم إلغاء الإيصال {number}.",
  "Could not void receipt.": "تعذر إلغاء الإيصال.",
  "Enter a reason before voiding this receipt.": "أدخل سبباً قبل إلغاء هذا الإيصال.",
  "Voided: {reason}": "ملغى: {reason}",
  "No reason recorded": "لم يُسجل سبب",
  "Void receipt": "إلغاء الإيصال",
  "Refund required": "مطلوب استرداد المبلغ",
  "Receipts with payments need refund handling before they can be voided.": "الإيصالات التي تحتوي على مدفوعات تحتاج إلى معالجة رد المبلغ قبل إلغائها.",
  "Mistake, duplicate, customer left...": "خطأ، مكرر، العميل غادر...",
  "Voiding...": "جار الإلغاء...",
  "Receipt {number} issued and paid.": "تم إصدار الإيصال {number} وتم الدفع.",

  "Live view": "العرض المباشر",
  "{count} total units": "{count} وحدات إجمالاً",
  "All": "الكل",
  "Available": "متاح",
  "Occupied": "مشغول",
  "Reserved": "محجوز",
  "Blocked": "محجوب",
  "All types": "كل الأنواع",
  "Search units": "بحث الوحدات",
  "Search units or guests...": "ابحث عن وحدة أو ضيف...",
  "Duration": "المدة",
  "Note": "ملاحظة",
  "Guest contact": "بيانات الضيف",
  "{count} seats": "{count} مقاعد",
  "Ready for walk-in": "جاهز لزيارة مباشرة",
  "{duration} planned · {amount} est.": "{duration} مخططة · تقدير {amount}",
  "No live controls while this unit is unavailable.": "لا توجد أدوات تشغيل مباشرة أثناء عدم إتاحة هذه الوحدة.",
  "Ends {value}": "ينتهي {value}",
  "Elapsed": "المنقضي",
  "Remaining": "المتبقي",
  "Overtime": "زيادة",
  "Checkout": "الدفع",
  "Food": "الطعام",
  "End options": "خيارات الإنهاء",
  "End and open checkout": "إنهاء وفتح الدفع",
  "Payment was already collected at the desk": "تم تحصيل الدفع مسبقاً في المكتب",
  "Walk-in duration must be at least 15 minutes.": "مدة الزيارة المباشرة يجب ألا تقل عن 15 دقيقة.",
  "Walk-in duration cannot exceed 720 minutes.": "مدة الزيارة المباشرة يجب ألا تتجاوز 720 دقيقة.",
  "Open receipt": "فتح الإيصال",
  "Open checkout": "فتح الدفع",
  "Ended {value}": "انتهت {value}",
  "No units match the current filters.": "لا توجد وحدات تطابق الفلاتر الحالية.",
  "Qty": "الكمية",
  "{quantity} x {name} · {price} each": "{quantity} × {name} · {price} لكل واحد",
  "Auto": "تلقائي",
  "Hard confirmation": "تأكيد نهائي",
  "I understand this action is destructive and should not be one click.": "أفهم أن هذا الإجراء حساس ولا يجب أن يتم بنقرة واحدة.",
  "Type {value} to confirm": "اكتب {value} للتأكيد",
  "Enter {value} exactly to enable the destructive action.": "اكتب {value} بالضبط لتفعيل الإجراء.",
  "Cancel": "إلغاء",
  "Dismiss": "إخفاء",
  "A new version of Gaamed is available": "يتوفر إصدار جديد من Gaamed",
  "Reload to get the latest fixes. Unsaved changes on this page will be lost.": "أعد التحميل للحصول على أحدث الإصلاحات. ستفقد التغييرات غير المحفوظة في هذه الصفحة.",
  "Reload": "إعادة التحميل",
  "Loading": "جار التحميل",
  "Saving...": "جار الحفظ...",
  "Saving…": "جار الحفظ…",
  "Working...": "جار العمل...",
  "Delete": "حذف",
  "Edit": "تعديل",
  "Hide": "إخفاء",
  "Show": "إظهار",
  "Online": "متصل",
  "Offline": "غير متصل",
  "approved": "معتمد",
  "pending": "قيد المراجعة",
  "rejected": "مرفوض",
  "suspended": "موقوف",
  "removed": "محذوف",
  "removal requested": "طلب حذف",
  "available": "متاح",
  "reserved": "محجوز",
  "blocked": "محجوب",
  "occupied": "مشغول",
  "offline": "خارج الخدمة",
  "console": "منصة ألعاب",
  "pc": "حاسوب",
  "vr": "واقع افتراضي",
  "simulator": "محاكي",
  "billiards": "بلياردو",
  "pingpong": "تنس طاولة",
  "room": "غرفة",
  "other": "أخرى",
  "Sunday": "الأحد",
  "Monday": "الاثنين",
  "Tuesday": "الثلاثاء",
  "Wednesday": "الأربعاء",
  "Thursday": "الخميس",
  "Friday": "الجمعة",
  "Saturday": "السبت",
  "Provider onboarding": "تهيئة المزود",
  "Signed in as": "مسجل الدخول باسم",
  "Edit registration": "تعديل التسجيل",
  "Refreshing...": "جار التحديث...",
  "Refresh status": "تحديث الحالة",
  "Next steps": "الخطوات التالية",
  "Admin note": "ملاحظة الإدارة",
  "No admin note yet. Refresh this page after gaamed reviews the registration.": "لا توجد ملاحظة من الإدارة بعد. حدّث الصفحة بعد مراجعة التسجيل.",
  "Pick a venue": "اختر مكاناً",
  "{count} venues": "{count} أماكن",
  "{count} venue": "{count} مكان",
  "{count} units": "{count} وحدات",
  "{count} unit": "{count} وحدة",
  "{count} free units": "{count} وحدات متاحة",
  "{count} free unit": "{count} وحدة متاحة",
  "{count} live sessions": "{count} جلسات مباشرة",
  "{count} live session": "{count} جلسة مباشرة",
  "{count} active sessions": "{count} جلسات نشطة",
  "{count} active session": "{count} جلسة نشطة",
  "{count} active": "{count} نشطة",
  "{count} blocked": "{count} محجوبة",
  "{count} scheduled": "{count} مجدولة",
  "{count} member": "{count} عضو",
  "{count} members": "{count} أعضاء",
  "{count} note": "{count} ملاحظة",
  "{count} notes": "{count} ملاحظات",
  "{count} record": "{count} سجل",
  "{count} records": "{count} سجلات",
  "{count} bookings": "{count} حجوزات",
  "{count} sessions": "{count} جلسات",
  "{count} visible booking": "{count} حجز ظاهر",
  "{count} visible bookings": "{count} حجوزات ظاهرة",
  "{count} game on record": "{count} لعبة مسجلة",
  "{count} games on record": "{count} ألعاب مسجلة",
  "{count} item": "{count} عنصر",
  "{count} items": "{count} عناصر",
  "{count} queued": "{count} في الانتظار",
  "Overview": "نظرة عامة",
  "Open now": "مفتوح الآن",
  "Closed": "مغلق",
  "Closed now": "مغلق الآن",
  "No venue selected": "لم يتم اختيار مكان",
  "Select a venue to begin operations.": "اختر مكاناً لبدء التشغيل.",
  "Venues": "الأماكن",
  "Units": "الوحدات",
  "Live now": "مباشر الآن",
  "Free": "متاح",
  "Live": "مباشر",
  "No units yet.": "لا توجد وحدات بعد.",
  "No units yet — add one above.": "لا توجد وحدات بعد — أضف وحدة من الأعلى.",
  "Venue operations": "تشغيل الأماكن",
  "Edit venue": "تعديل المكان",
  "Hours": "الساعات",
  "Blackouts": "فترات الإغلاق",
  "+ Venue": "+ مكان",
  "+ Unit": "+ وحدة",
  "This physical venue is not approved for operations yet. Admin review is required before units, hours, bookings, sessions, games, or food go live.": "هذا المكان الفعلي غير معتمد للتشغيل بعد. يلزم اعتماد الإدارة قبل تشغيل الوحدات والساعات والحجوزات والجلسات والألعاب والطعام.",
  "Submit for approval": "إرسال للاعتماد",
  "New venue request": "طلب مكان جديد",
  "Removal approval": "اعتماد الحذف",
  "Current request: {note}": "الطلب الحالي: {note}",
  "Explain why this physical venue should be removed from gaamed.": "اشرح سبب حذف هذا المكان الفعلي من gaamed.",
  "Sending request...": "جار إرسال الطلب...",
  "Request removal approval": "طلب اعتماد الحذف",
  "Save venue": "حفظ المكان",
  "Create unit": "إنشاء وحدة",
  "Save unit": "حفظ الوحدة",
  "Edit {name}": "تعديل {name}",
  "Add unit to {name}": "إضافة وحدة إلى {name}",
  "Deleting...": "جار الحذف...",
  "Delete unit": "حذف الوحدة",
  "Only approved venues can request removal.": "يمكن طلب حذف الأماكن المعتمدة فقط.",
  "Add a removal note for admin review before requesting venue removal.": "أضف ملاحظة حذف لمراجعة الإدارة قبل إرسال الطلب.",
  "Send removal request": "إرسال طلب الحذف",
  "Type {value} to send this removal request": "اكتب {value} لإرسال طلب الحذف",
  "Admins review this request before the physical venue can be removed, so do not submit it casually.": "تراجع الإدارة هذا الطلب قبل حذف المكان الفعلي، لذلك لا ترسله دون قصد.",
  "This sends {name} to admins for manual removal approval using the note you entered.": "يرسل هذا {name} إلى الإدارة لاعتماد الحذف يدوياً حسب الملاحظة التي أدخلتها.",
  "Request removal approval for {name}?": "طلب اعتماد حذف {name}?",
  "Type {value} to permanently delete this unit": "اكتب {value} لحذف هذه الوحدة نهائياً",
  "The unit, its photos, and its pricing configuration are removed from this venue.": "سيتم حذف الوحدة وصورها وإعدادات تسعيرها من هذا المكان.",
  "This permanently deletes {unitName} from {venueName}.": "يحذف هذا {unitName} نهائياً من {venueName}.",
  "Delete {name}?": "حذف {name}?",
  "Bookings": "الحجوزات",
  "New booking": "حجز جديد",
  "Select a venue before scheduling.": "اختر مكاناً قبل الجدولة.",
  "Calendar operations": "تشغيل التقويم",
  "Live sessions": "الجلسات المباشرة",
  "Start walk-in": "بدء زيارة مباشرة",
  "Select a venue to run live sessions.": "اختر مكاناً لتشغيل الجلسات المباشرة.",
  "Floor operations": "تشغيل الصالة",
  "Internal notes": "ملاحظات داخلية",
  "Operational note feed": "سجل الملاحظات التشغيلية",
  "Showing notes across all provider venues.": "عرض الملاحظات عبر كل أماكن المزود.",
  "Team access": "صلاحيات الفريق",
  "Workspace members": "أعضاء مساحة العمل",
  "Provider workspace": "مساحة عمل المزود",
  "Select a venue first to manage its games menu.": "اختر مكاناً أولاً لإدارة قائمة ألعابه.",
  "Select a venue first to manage its food menu.": "اختر مكاناً أولاً لإدارة قائمة طعامه.",
  "Offline readiness": "جاهزية العمل دون اتصال",
  "Offline mode is active.": "وضع عدم الاتصال نشط.",
  "Syncing queued provider changes.": "جار مزامنة تغييرات المزود المنتظرة.",
  "{count} provider change waiting to sync.": "تغيير مزود واحد بانتظار المزامنة.",
  "{count} provider changes waiting to sync.": "{count} تغييرات مزود بانتظار المزامنة.",
  "Provider sync needs attention.": "مزامنة المزود تحتاج مراجعة.",
  "Cached provider pages remain available offline. New provider changes are queued locally and replay when the connection returns.": "تبقى صفحات المزود المخزنة متاحة دون اتصال. تُحفظ التغييرات الجديدة محلياً وتُعاد مزامنتها عند عودة الاتصال.",
  "Sync issue: {error}": "مشكلة مزامنة: {error}",
  "Syncing...": "جار المزامنة...",
  "Sync now": "زامن الآن",
  "Admin": "مدير",
  "Support": "دعم",
  "Provider owner": "مالك المزود",
  "Provider staff": "موظف المزود",
  "Customer": "عميل",
  "Unknown role": "دور غير معروف",
  "Owner controls": "تحكم المالك",
  "Read only": "قراءة فقط",
  "Select venue": "اختر مكاناً",
  "Booking history": "سجل الحجوزات",
  "Provider note feed": "سجل ملاحظات المزود",
  "Venue note feed": "سجل ملاحظات المكان",
  "schedulable units": "وحدات قابلة للجدولة",
  "Workspace status and current venue focus.": "حالة مساحة العمل وتركيز المكان الحالي.",
  "Locations, availability, and unit inventory.": "المواقع والتوفر ومخزون الوحدات.",
  "Calendar scheduling and booking control.": "جدولة التقويم والتحكم بالحجوزات.",
  "Walk-ins, occupancy, and live floor activity.": "الزيارات المباشرة والإشغال ونشاط الصالة.",
  "Operational note feed across bookings and sessions.": "سجل ملاحظات تشغيلي عبر الحجوزات والجلسات.",
  "Provider staff accounts and workspace access.": "حسابات موظفي المزود وصلاحيات مساحة العمل.",
  "Booking history and customer records.": "سجل الحجوزات وبيانات العملاء.",
  "Manage the games library for each venue.": "إدارة مكتبة الألعاب لكل مكان.",
  "Manage food and drink items for each venue.": "إدارة عناصر الطعام والمشروبات لكل مكان.",
  "Policy, VAT, booking rules, and session limits.": "السياسات والضريبة وقواعد الحجز وحدود الجلسات.",
  "No venues yet. Create the first venue to establish the provider workspace.": "لا توجد أماكن بعد. أنشئ أول مكان لتأسيس مساحة عمل المزود.",
  "Selected": "محدد",
  "View booking": "عرض الحجز",
  "Manage unit": "إدارة الوحدة",
  "Could not load policy settings.": "تعذر تحميل إعدادات السياسة.",
  "Policy settings saved.": "تم حفظ إعدادات السياسة.",
  "Could not save policy settings.": "تعذر حفظ إعدادات السياسة.",
  "Loading policy settings…": "جار تحميل إعدادات السياسة…",
  "Could not load settings.": "تعذر تحميل الإعدادات.",
  "Tax": "الضريبة",
  "VAT enabled": "تفعيل الضريبة",
  "VAT %": "نسبة الضريبة %",
  "Booking rules": "قواعد الحجز",
  "Min booking duration (minutes)": "أقل مدة حجز (بالدقائق)",
  "Max booking duration (minutes)": "أقصى مدة حجز (بالدقائق)",
  "Cancellation window (minutes before start)": "مهلة الإلغاء (دقائق قبل البدء)",
  "Auto-confirm threshold %": "حد التأكيد التلقائي %",
  "Bookings under this price % of max rate are auto-confirmed": "الحجوزات تحت هذه النسبة من أعلى سعر تُؤكد تلقائياً",
  "Set to 100 to auto-confirm all bookings.": "اجعلها 100 لتأكيد كل الحجوزات تلقائياً.",
  "Session rules": "قواعد الجلسات",
  "Max session duration (minutes)": "أقصى مدة جلسة (بالدقائق)",
  "Max concurrent active sessions (blank = unlimited)": "أقصى جلسات نشطة بالتوازي (فارغ = بلا حد)",
  "Unlimited": "بلا حد",
  "No-show policy": "سياسة عدم الحضور",
  "No-show penalty %": "غرامة عدم الحضور %",
  "0 = no penalty. Charged from booking total on no-show.": "0 = بلا غرامة. تُحتسب من إجمالي الحجز عند عدم الحضور.",
  "Could not search for this address.": "تعذر البحث عن العنوان ده.",
  "No map result found for this venue.": "لم نجد نتيجة خريطة للمكان ده.",
  "The map result did not include a usable pin.": "نتيجة الخريطة لا تحتوي على دبوس صالح للاستخدام.",
  "Search for the venue or drop a pin before submitting this physical venue for approval.": "ابحث عن المكان أو ضع دبوساً قبل إرسال هذا المكان الفعلي للاعتماد.",
  "Enter a venue address or area to find a map pin.": "أدخل عنوان المكان أو المنطقة للعثور على دبوس الخريطة.",
  "Could not set the map pin.": "تعذر ضبط دبوس الخريطة.",
  "Venue name": "اسم المكان",
  "Downtown flagship": "فرع وسط البلد الرئيسي",
  "City": "المدينة",
  "Cairo": "القاهرة",
  "Timezone": "المنطقة الزمنية",
  "Address": "العنوان",
  "Tahrir Square": "ميدان التحرير",
  "Set your password": "عيّن كلمة المرور",
  "Choose a password to activate your account. This one-time link expires after 7 days.": "اختر كلمة مرور لتفعيل حسابك. هذا الرابط صالح لاستخدام واحد وينتهي بعد ٧ أيام.",
  "Your password is set. Sign in to continue to your workspace.": "تم تعيين كلمة المرور. سجّل الدخول للانتقال إلى مساحة عملك.",
  "Before inviting the owner": "قبل دعوة صاحب المكان",
  "Venue setup checklist": "قائمة تجهيز المكان",
  "Venue photos": "صور المكان",
  "Logo": "الشعار",
  "Priced units": "الوحدات والأسعار",
  "Opening hours": "ساعات العمل",
  "Prepare the photos, units, prices, games, menu and hours now. This venue stays hidden from customers until admin approval publishes it.": "جهّز الصور والوحدات والأسعار والألعاب والقائمة وساعات العمل الآن. سيظل المكان مخفيًا عن العملاء حتى تنشره موافقة الإدارة.",
  "Confirm your opening hours, available games and menu before the venue is approved.": "تأكد من ساعات العمل والألعاب المتاحة والقائمة قبل الموافقة على المكان.",
  "Confirm the actual schedule, available games and menu. Exit assistance, approve the venue to publish it, then send the owner a setup link.": "راجع ساعات العمل والألعاب المتاحة والقائمة. اخرج من وضع المساعدة، ووافق على المكان لنشره، ثم أرسل لصاحب المكان رابط إعداد الحساب.",
  "This venue cannot be configured while removal is pending or complete.": "لا يمكن تعديل إعدادات هذا المكان أثناء انتظار حذفه أو بعد حذفه.",
  "Map pin": "دبوس الخريطة",
  "Search a venue or paste a Google Maps share link": "ابحث عن مكان أو الصق رابط مشاركته من خرائط Google",
  "Search map": "ابحث في الخريطة",
  "Press Enter to search. Google Maps share links set the pin automatically; for other searches, choose a match or click the map.": "اضغط Enter للبحث. روابط مشاركة خرائط Google تحدد الدبوس تلقائياً؛ ولعمليات البحث الأخرى اختر نتيجة أو انقر الخريطة.",
  "Choose a location result": "اختر نتيجة الموقع",
  "Choose the correct match": "اختر الموقع الصحيح",
  "Use this location": "استخدم هذا الموقع",
  "Paste a Google Maps share link to set the pin, search by venue name, or click the map to place it manually.": "الصق رابط مشاركة من خرائط Google لتحديد الدبوس، أو ابحث باسم المكان، أو انقر الخريطة لتحديده يدوياً.",
  "This Google Maps share link could not be resolved. Try a new share link, search by venue name, or place the pin manually.": "تعذر تحديد الموقع من رابط مشاركة خرائط Google. جرّب رابط مشاركة جديداً أو ابحث باسم المكان أو ضع الدبوس يدوياً.",
  "That location input is malformed. Paste a Google Maps share link, or enter an Egypt address or landmark.": "إدخال الموقع غير صالح. الصق رابط مشاركة من خرائط Google أو أدخل عنواناً أو معلماً في مصر.",
  "That location input is not valid. Enter an Egypt address, landmark, or a Google Maps share link.": "إدخال الموقع غير صالح. أدخل عنواناً أو معلماً في مصر أو رابط مشاركة من خرائط Google.",
  "That location is outside Egypt. Choose an Egypt address or landmark, or pin the venue manually within the Egypt map bounds.": "هذا الموقع خارج مصر. اختر عنواناً أو معلماً في مصر، أو ضع الدبوس يدوياً داخل حدود الخريطة المصرية.",
  "No Egypt matches were found. Try a nearby landmark, district, or a more complete address.": "لم نجد نتائج في مصر. جرّب معلماً أو حياً قريباً أو عنواناً أكثر تفصيلاً.",
  "Search address, mall, district, or landmark": "ابحث بعنوان أو مول أو حي أو معلم",
  "Finding...": "جار البحث...",
  "Find pin": "العثور على الدبوس",
  "Drop venue map pin": "ضع دبوس مكان على الخريطة",
  "Click map to drop pin": "انقر الخريطة لوضع الدبوس",
  "Pin set near {latitude}, {longitude}": "تم ضبط الدبوس قرب {latitude}, {longitude}",
  "Search for the venue or drop a pin near the physical entrance.": "ابحث عن المكان أو ضع دبوساً قرب المدخل الفعلي.",
  "Matched: {result}": "النتيجة: {result}",
  "Admin request note": "ملاحظة طلب للإدارة",
  "Why should this physical venue be added? Include ownership, address confidence, and launch context.": "لماذا يجب إضافة هذا المكان الفعلي؟ أضف الملكية وثقة العنوان وسياق الإطلاق.",
  "Unit name": "اسم الوحدة",
  "PS5 Arena": "ساحة PS5",
  "Unit type": "نوع الوحدة",
  "Status": "الحالة",
  "Capacity": "السعة",
  "Base hourly rate": "السعر الأساسي للساعة",
  "Peak pricing windows": "فترات تسعير الذروة",
  "Override the base rate during specific venue-local hours.": "تجاوز السعر الأساسي خلال ساعات محلية محددة للمكان.",
  "{timezone} local time.": "بتوقيت {timezone} المحلي.",
  "Add peak window": "إضافة فترة ذروة",
  "No peak pricing yet. Add a window to raise rates during busy hours.": "لا يوجد تسعير ذروة بعد. أضف فترة لرفع الأسعار وقت الازدحام.",
  "Day": "اليوم",
  "Starts": "يبدأ",
  "Ends": "ينتهي",
  "Hourly rate": "سعر الساعة",
  "Remove": "إزالة",
  "Mark as VIP unit": "تمييز كوحدة VIP",
  "Discard window": "تجاهل الفترة",
  "This only removes the unsaved pricing row from the current draft until you add it again.": "هذا يزيل صف التسعير غير المحفوظ من المسودة الحالية حتى تضيفه مرة أخرى.",
  "This removes the draft pricing window for {window}.": "هذا يزيل مسودة فترة التسعير لـ {window}.",
  "Discard this peak pricing window?": "تجاهل فترة تسعير الذروة هذه؟",
  "Availability": "التوفر",
  "Weekly operating hours define when this venue can accept bookings.": "ساعات التشغيل الأسبوعية تحدد متى يمكن للمكان قبول الحجوزات.",
  "Opens": "يفتح",
  "Closes": "يغلق",
  "Saving availability...": "جار حفظ التوفر...",
  "Save availability": "حفظ التوفر",
  "Blackout windows": "فترات الإغلاق",
  "Block bookings, walk-ins, and session extensions during maintenance, private events, or venue-wide closures.": "امنع الحجوزات والزيارات المباشرة وتمديد الجلسات أثناء الصيانة أو الفعاليات الخاصة أو إغلاق المكان بالكامل.",
  "Starts at": "يبدأ في",
  "Ends at": "ينتهي في",
  "Reason": "السبب",
  "Deep clean, private booking, hardware migration...": "تنظيف عميق، حجز خاص، نقل أجهزة...",
  "Saving blackout...": "جار حفظ الإغلاق...",
  "Add blackout window": "إضافة فترة إغلاق",
  "No reason added yet.": "لم يُضف سبب بعد.",
  "Removing...": "جار الإزالة...",
  "Remove blackout": "إزالة الإغلاق",
  "Bookings, walk-ins, and session extensions can use this time block again as soon as the venue refreshes.": "ستتمكن الحجوزات والزيارات المباشرة وتمديد الجلسات من استخدام هذا الوقت بعد تحديث المكان.",
  "This removes the blackout scheduled from {start} to {end}.": "هذا يزيل فترة الإغلاق المجدولة من {start} إلى {end}.",
  "Remove blackout window?": "إزالة فترة الإغلاق؟",
  "No blackout windows yet. Add one when the venue needs maintenance time or a private closure block.": "لا توجد فترات إغلاق بعد. أضف واحدة عند الحاجة لصيانة أو إغلاق خاص.",
  "Photos": "الصور",
  "{used}/{limit} used": "{used}/{limit} مستخدمة",
  "max": "حد أقصى",
  "WebP compressed in browser": "ضغط WebP داخل المتصفح",
  "Upload photo": "رفع صورة",
  "Primary": "أساسية",
  "Set primary": "اجعلها أساسية",
  "No photos yet.": "لا توجد صور بعد.",
  "Upload photos for {name}": "رفع صور {name}",
  "Click or drop images anywhere to upload": "انقر أو أسقط الصور في أي مكان للرفع",
  "Drop images to upload": "أسقط الصور للرفع",
  "Uploading photos...": "جار رفع الصور...",
  "Only image files can be uploaded.": "يمكن رفع ملفات الصور فقط.",
  "Photo limit reached — some images were not uploaded.": "تم بلوغ حد الصور — لم تُرفع بعض الصور.",
  "Delete photo": "حذف الصورة",
  "The image will disappear from customer-facing galleries and provider workspace cards.": "ستختفي الصورة من معارض العملاء وبطاقات مساحة عمل المزود.",
  "This permanently removes the selected photo from {name}.": "يحذف هذا الصورة المحددة نهائياً من {name}.",
  "Delete a photo from {name}?": "حذف صورة من {name}؟",
  "Owner": "مالك",
  "Staff": "موظف",
  "Manager": "مدير",
  "Front desk": "موظف الاستقبال",
  "Workspace role": "دور مساحة العمل",
  "Role": "الدور",
  "Change role": "تغيير الدور",
  "Updating role...": "جار تحديث الدور...",
  "Updated team role.": "تم تحديث دور عضو الفريق.",
  "Unable to update team role.": "تعذر تحديث دور عضو الفريق.",
  "Saved team role offline. It will sync when connection returns.": "حُفظ دور عضو الفريق دون اتصال. ستتم المزامنة عند عودة الاتصال.",
  "Elevation": "رفع الصلاحية",
  "Manager approval required": "مطلوب موافقة المدير",
  "This action needs {role} approval. Ask a manager or owner to enter their credentials — approved actions stay unlocked for 10 minutes.": "هذا الإجراء يتطلب موافقة {role}. اطلب من مدير أو مالك إدخال بيانات اعتماده — تبقى الإجراءات المعتمدة متاحة لمدة 10 دقائق.",
  "Approver email": "البريد الإلكتروني للموافِق",
  "Approver password": "كلمة مرور الموافِق",
  "Approve action": "اعتماد الإجراء",
  "Approving...": "جار الاعتماد...",
  "Could not verify approver credentials.": "تعذر التحقق من بيانات اعتماد الموافِق.",
  "Only owners can change these settings.": "يمكن للمالكين فقط تغيير هذه الإعدادات.",
  "Only owners can change business, finance, and policy settings.": "يمكن للمالكين فقط تغيير إعدادات العمل والمالية والسياسات.",
  "Only owners can change venue setup.": "يمكن للمالكين فقط تغيير إعداد المكان.",
  "Provider owners can add staff accounts who share the same workspace.": "يمكن لمالكي المزود إضافة حسابات موظفين تشارك نفس مساحة العمل.",
  "Loading team access...": "جار تحميل صلاحيات الفريق...",
  "Revoked": "ملغى",
  "Revoke access": "إلغاء الصلاحية",
  "Type {value} to revoke this staff account": "اكتب {value} لإلغاء صلاحية هذا الموظف",
  "Revoked staff lose provider workspace access until an owner adds them again.": "يفقد الموظف الملغى صلاحية مساحة عمل المزود حتى يضيفه المالك مرة أخرى.",
  "This removes provider workspace access for {name}.": "هذا يزيل صلاحية مساحة عمل المزود عن {name}.",
  "Revoke access for {name}?": "إلغاء صلاحية {name}؟",
  "Revoking...": "جار الإلغاء...",
  "Revoke": "إلغاء",
  "No staff accounts yet. Add the first provider staff member below.": "لا توجد حسابات موظفين بعد. أضف أول موظف مزود أدناه.",
  "Staff name": "اسم الموظف",
  "Venue Supervisor": "مشرف المكان",
  "Staff email": "بريد الموظف",
  "Temporary password": "كلمة مرور مؤقتة",
  "Adding staff...": "جار إضافة الموظف...",
  "Add staff member": "إضافة موظف",
  "Only provider owners can add or revoke staff access. Provider staff can still view the current team roster.": "يمكن لمالكي المزود فقط إضافة أو إلغاء صلاحيات الموظفين. لا يزال موظفو المزود قادرين على عرض قائمة الفريق الحالية.",
  "Booking note": "ملاحظة حجز",
  "Booking status note": "ملاحظة حالة الحجز",
  "Session note": "ملاحظة جلسة",
  "Session end note": "ملاحظة نهاية الجلسة",
  "Payment note": "ملاحظة دفع",
  "Unable to load internal notes.": "تعذر تحميل الملاحظات الداخلية.",
  "Booking notes, status reasons, handoff notes, and payment notes land in one provider feed so staff can keep context without opening each record.": "تجتمع ملاحظات الحجوزات وأسباب الحالة وملاحظات التسليم والدفع في سجل واحد حتى يحافظ الموظفون على السياق دون فتح كل سجل.",
  "All provider venues": "كل أماكن المزود",
  "Loading internal notes...": "جار تحميل الملاحظات الداخلية...",
  "No internal notes yet. Booking notes, session notes, end notes, and payment notes will appear here.": "لا توجد ملاحظات داخلية بعد. ستظهر هنا ملاحظات الحجوزات والجلسات والنهايات والدفع.",
  "Email {value}": "البريد {value}",
  "Phone {value}": "الهاتف {value}",
  "Booking linked": "مرتبط بحجز",
  "No booking link": "لا يوجد رابط حجز",
  "Session linked": "مرتبط بجلسة",
  "No session link": "لا يوجد رابط جلسة",
  "Could not load games.": "تعذر تحميل الألعاب.",
  "Could not save game.": "تعذر حفظ اللعبة.",
  "Could not delete game.": "تعذر حذف اللعبة.",
  "Could not update game.": "تعذر تحديث اللعبة.",
  "Assign this game to at least one unit.": "اربط هذه اللعبة بوحدة واحدة على الأقل.",
  "Search for at least two characters.": "ابحث بحرفين على الأقل.",
  "Could not search RAWG right now.": "تعذر البحث في RAWG حالياً.",
  "Choose at least one unit before adding a game.": "اختر وحدة واحدة على الأقل قبل إضافة اللعبة.",
  "Could not import game from RAWG.": "تعذر استيراد اللعبة من RAWG.",
  "Assign to units": "ربط بالوحدات",
  "Editing unit scope for {name}": "تعديل نطاق الوحدات لـ {name}",
  "Use the active venue selector above; games are scoped to this venue's units.": "استخدم اختيار المكان النشط بالأعلى؛ الألعاب مرتبطة بوحدات هذا المكان.",
  "Save unit scope": "حفظ نطاق الوحدات",
  "Game catalog": "كتالوج الألعاب",
  "RAWG search": "بحث RAWG",
  "Search and assign games": "ابحث واربط الألعاب",
  "Search RAWG, pick the correct title, then assign it to the units that actually have it installed.": "ابحث في RAWG، اختر العنوان الصحيح، ثم اربطه بالوحدات التي تحتوي عليه فعلاً.",
  "Search RAWG": "بحث RAWG",
  "Search game title...": "ابحث باسم اللعبة...",
  "Searching RAWG...": "جار البحث في RAWG...",
  "Fresh RAWG results": "نتائج RAWG جديدة",
  "Cached results": "نتائج مخزنة",
  "Local catalog results": "نتائج الكتالوج المحلي",
  "Search results": "نتائج البحث",
  "{count} results": "{count} نتائج",
  "Search RAWG to add real game artwork, platforms, and descriptions.": "ابحث في RAWG لإضافة صور ومنصات وأوصاف حقيقية للألعاب.",
  "Already in venue": "موجودة في المكان",
  "Update units": "تحديث الوحدات",
  "Add to units": "إضافة للوحدات",
  "Venue game library": "مكتبة ألعاب المكان",
  "No games yet. Search RAWG and assign the first game to this venue's units.": "لا توجد ألعاب بعد. ابحث في RAWG واربط أول لعبة بوحدات هذا المكان.",
  "Edit units": "تعديل الوحدات",
  "+ Add game": "+ إضافة لعبة",
  "Loading games…": "جار تحميل الألعاب…",
  "Edit game": "تعديل لعبة",
  "New game": "لعبة جديدة",
  "Title *": "العنوان *",
  "Category": "الفئة",
  "action": "أكشن",
  "adventure": "مغامرة",
  "fighting": "قتال",
  "fps": "تصويب",
  "racing": "سباق",
  "rpg": "تقمص أدوار",
  "sports": "رياضة",
  "strategy": "استراتيجية",
  "Platform": "المنصة",
  "e.g. PlayStation 5, PC": "مثلاً PlayStation 5 أو PC",
  "Upload image": "رفع صورة",
  "Uploading image…": "جار رفع الصورة…",
  "JPG, PNG, WebP, GIF or AVIF up to 3 MB. Large images are converted in your browser.": "JPG أو PNG أو WebP أو GIF أو AVIF حتى 3 ميجابايت. الصور الكبيرة تُحوّل داخل المتصفح.",
  "Image URL": "رابط الصورة",
  "Current image": "الصورة الحالية",
  "Description": "الوصف",
  "Assigned units": "الوحدات المرتبطة",
  "{count} selected": "{count} محددة",
  "Create units before assigning games.": "أنشئ وحدات قبل ربط الألعاب.",
  "Active (visible to customers)": "نشطة (ظاهرة للعملاء)",
  "Save changes": "حفظ التغييرات",
  "Add game": "إضافة لعبة",
  "No games yet. Add the first game for this venue.": "لا توجد ألعاب بعد. أضف أول لعبة لهذا المكان.",
  "Hidden": "مخفية",
  "Unit scoped": "مرتبطة بوحدات",
  "No units assigned": "لا توجد وحدات مرتبطة",
  "+{count} more": "+{count} أخرى",
  "Delete game": "حذف اللعبة",
  "Type {value} to permanently delete this game": "اكتب {value} لحذف هذه اللعبة نهائياً",
  "This game disappears from the venue menu immediately and the change will still sync if it was queued offline.": "ستختفي هذه اللعبة من قائمة المكان فوراً وسيظل التغيير متزامناً إن كان محفوظاً دون اتصال.",
  "This permanently removes {name} from {venueName}.": "يحذف هذا {name} نهائياً من {venueName}.",
  "Could not load food menu.": "تعذر تحميل قائمة الطعام.",
  "Could not save food item.": "تعذر حفظ عنصر الطعام.",
  "Could not delete item.": "تعذر حذف العنصر.",
  "Could not update item.": "تعذر تحديث العنصر.",
  "+ Add item": "+ إضافة عنصر",
  "Loading menu…": "جار تحميل القائمة…",
  "Edit item": "تعديل عنصر",
  "New item": "عنصر جديد",
  "Name *": "الاسم *",
  "Price (EGP) *": "السعر (جنيه) *",
  "Available for ordering": "متاح للطلب",
  "Add item": "إضافة عنصر",
  "No menu items yet. Add your first food or drink item.": "لا توجد عناصر قائمة بعد. أضف أول طعام أو مشروب.",
  "beverages": "مشروبات",
  "snacks": "وجبات خفيفة",
  "meals": "وجبات",
  "desserts": "حلويات",
  "Unavailable": "غير متاح",
  "Mark unavailable": "اجعله غير متاح",
  "Mark available": "اجعله متاحاً",
  "Delete item": "حذف العنصر",
  "Type {value} to permanently delete this item": "اكتب {value} لحذف هذا العنصر نهائياً",
  "This item disappears from the venue menu immediately and the change will still sync if it was queued offline.": "سيختفي هذا العنصر من قائمة المكان فوراً وسيظل التغيير متزامناً إن كان محفوظاً دون اتصال.",
  "Book time on {name} without leaving the workspace.": "احجز وقتاً في {name} دون مغادرة مساحة العمل.",
  "Select a venue before editing bookings.": "اختر مكاناً قبل تعديل الحجوزات.",
  "Unit": "الوحدة",
  "Customer name": "اسم العميل",
  "Customer email": "بريد العميل",
  "Booking contact email": "بريد التواصل للحجز",
  "Staff check-in QR": "رمز تسجيل الحضور للموظفين",
  "Use the provider scanner to check in this unlinked booking.": "استخدم ماسح مقدم الخدمة لتسجيل حضور هذا الحجز غير المرتبط بحساب.",
  "Customer check-in QR": "رمز تسجيل حضور العميل",
  "Show this to the customer — they scan it with the gaamed app.": "اعرض هذا الرمز للعميل لمسحه باستخدام تطبيق جامد.",
  "Contact only. Changing this email does not change sign-in or booking access.": "للتواصل فقط. تغيير البريد لا يغيّر تسجيل الدخول أو صلاحية الوصول للحجز.",
  "Linked account history": "سجل الحساب المرتبط",
  "Linked customer account": "حساب العميل المرتبط",
  "Account link information is unavailable.": "معلومات ربط الحساب غير متاحة.",
  "Registered customer": "عميل مسجل",
  "No linked account. Contact details do not grant app access.": "لا يوجد حساب مرتبط. بيانات التواصل لا تمنح صلاحية الوصول من التطبيق.",
  "This account link cannot be edited by staff.": "لا يمكن للموظفين تغيير ارتباط هذا الحساب.",
  "Customer phone": "هاتف العميل",
  "Notes": "ملاحظات",
  "Operational notes for {unit}.": "ملاحظات تشغيلية لـ {unit}.",
  "{label}...": "{label}...",
  "Pending": "معلق",
  "Confirmed": "مؤكد",
  "confirmed": "مؤكد",
  "cancelled": "ملغى",
  "no show": "عدم حضور",
  "checked in": "تم الدخول",
  "completed": "مكتمل",
  "expired": "منتهية",
  "Cancelled": "ملغى",
  "Rejected": "مرفوض",
  "No-show": "عدم حضور",
  "Checked in": "تم الدخول",
  "Completed": "مكتمل",
  "Expired": "منتهية",
  "Loading detail…": "جار تحميل التفاصيل…",
  "No bookings.": "لا توجد حجوزات.",
  "Sessions": "الجلسات",
  "Reviews": "التقييمات",
  "No contact": "لا توجد وسيلة تواصل",
  "Last seen": "آخر ظهور",
  "Unique customers derived from booking history across all your venues.": "عملاء فريدون مستخرجون من سجل الحجوزات عبر كل أماكنك.",
  "Search by name, email, or phone…": "ابحث بالاسم أو البريد أو الهاتف…",
  "Loading…": "جار التحميل…",
  "No customers match that search.": "لا يوجد عملاء يطابقون البحث.",
  "No bookings yet — customers will appear here once they book.": "لا توجد حجوزات بعد — سيظهر العملاء هنا عند الحجز.",
  "Save booking": "حفظ الحجز",
  "Create booking": "إنشاء حجز",
  "Edit booking": "تعديل الحجز",
  "Booking detail": "تفاصيل الحجز",
  "Pick a booking from the calendar to inspect or change it. Select an empty slot to create a new booking in place.": "اختر حجزاً من التقويم لفحصه أو تغييره. اختر خانة فارغة لإنشاء حجز مكانها.",
  "Visible bookings": "الحجوزات الظاهرة",
  "Bookings calendar": "تقويم الحجوزات",
  "Drive provider-side booking work from a day or week calendar without leaving the new stack.": "أدر أعمال حجوزات المزود من تقويم يومي أو أسبوعي دون مغادرة الواجهة الجديدة.",
  "Week": "الأسبوع",
  "Prev": "السابق",
  "Today": "اليوم",
  "Next": "التالي",
  "Unit filter": "فلتر الوحدة",
  "All units": "كل الوحدات",
  "Select a venue first. The venue picker above acts as the primary booking filter.": "اختر مكاناً أولاً. يعمل اختيار المكان أعلاه كفلتر الحجز الأساسي.",
  "Add at least one unit before scheduling bookings for {name}.": "أضف وحدة واحدة على الأقل قبل جدولة حجوزات {name}.",
  "Loading bookings for the current calendar window...": "جار تحميل الحجوزات لنافذة التقويم الحالية...",
  "Search by customer, unit, status…": "ابحث بالعميل أو الوحدة أو الحالة…",
  "No bookings match your search.": "لا توجد حجوزات تطابق بحثك.",
  "No bookings in the current range.": "لا توجد حجوزات في الفترة الحالية.",
  "Clear search": "مسح البحث",
  "Print receipt": "طباعة الإيصال",
  "Customer history": "سجل العميل",
  "Matched by email": "تطابق عبر البريد",
  "Matched by phone": "تطابق عبر الهاتف",
  "Matched by customer name": "تطابق عبر اسم العميل",
  "Bookings and sessions from this provider workspace": "حجوزات وجلسات من مساحة عمل هذا المزود",
  "{count} prior touchpoint": "{count} نقطة تواصل سابقة",
  "{count} prior touchpoints": "{count} نقاط تواصل سابقة",
  "Loading customer history...": "جار تحميل سجل العميل...",
  "No prior bookings or sessions for this customer in this provider workspace yet.": "لا توجد حجوزات أو جلسات سابقة لهذا العميل في مساحة عمل المزود بعد.",
  "Previous bookings": "حجوزات سابقة",
  "No prior bookings found.": "لم يتم العثور على حجوزات سابقة.",
  "Previous sessions": "جلسات سابقة",
  "No prior sessions found.": "لم يتم العثور على جلسات سابقة.",
  "View customer": "عرض العميل",
  "Venue": "المكان",
  "Email": "البريد",
  "Phone": "الهاتف",
  "Scheduled total": "الإجمالي المجدول",
  "Calculated from the unit base rate and any peak pricing windows.": "محسوب من السعر الأساسي للوحدة وأي فترات تسعير ذروة.",
  "Copy check-in QR": "نسخ QR الدخول",
  "Live session": "جلسة مباشرة",
  "Stop the session from the live sessions panel to complete the booking and release the unit.": "أوقف الجلسة من لوحة الجلسات المباشرة لإكمال الحجز وتحرير الوحدة.",
  "Action note": "ملاحظة الإجراء",
  "Optional reason for the next status change.": "سبب اختياري لتغيير الحالة التالي.",
  "Start session": "بدء الجلسة",
  "Confirm": "تأكيد",
  "Reject": "رفض",
  "Choose an available menu item before placing an order.": "اختر عنصراً متاحاً من القائمة قبل إرسال الطلب.",
  "Food order placed.": "تم إرسال طلب الطعام.",
  "Could not place the food order.": "تعذر إرسال طلب الطعام.",
  "Order marked {status}.": "تم وضع الطلب كـ {status}.",
  "Could not update food order status.": "تعذر تحديث حالة طلب الطعام.",
  "Start walk-ins, watch live occupancy, and stop sessions without leaving the provider workspace.": "ابدأ الزيارات المباشرة وراقب الإشغال وأوقف الجلسات دون مغادرة مساحة عمل المزود.",
  "Use a free unit in the selected venue and give the session a planned duration so overtime is visible.": "استخدم وحدة متاحة في المكان المختار وحدد مدة مخططة للجلسة حتى تظهر الزيادة بوضوح.",
  "Select a venue before starting walk-in sessions.": "اختر مكاناً قبل بدء جلسات الزيارة المباشرة.",
  "No units are currently free for walk-ins in {name}.": "لا توجد وحدات متاحة حالياً للزيارات المباشرة في {name}.",
  "Planned duration": "المدة المخططة",
  "Pricing preview": "معاينة التسعير",
  "Base {amount}/hr": "أساسي {amount}/ساعة",
  "planned {amount}": "مخطط {amount}",
  "No peak pricing windows configured for this unit.": "لا توجد فترات تسعير ذروة لهذه الوحدة.",
  "Optional session note.": "ملاحظة جلسة اختيارية.",
  "Active now": "نشط الآن",
  "Live occupancy is derived from these sessions instead of manual unit edits.": "يُستخرج الإشغال المباشر من هذه الجلسات بدلاً من تعديل الوحدات يدوياً.",
  "No active sessions in the current venue.": "لا توجد جلسات نشطة في المكان الحالي.",
  "No games assigned": "لا توجد ألعاب مرتبطة",
  "Show fewer": "عرض أقل",
  "Show {count} more": "عرض {count} أخرى",
  "booking": "حجز",
  "walk in": "زيارة مباشرة",
  "Active": "نشط",
  "Payment pending": "الدفع معلق",
  "Started {value}": "بدأ {value}",
  "Planned end": "النهاية المخططة",
  "Open ended": "مفتوح النهاية",
  "Elapsed {value}": "المنقضي {value}",
  "Live total {value}": "الإجمالي المباشر {value}",
  "Linked to booking": "مرتبط بحجز",
  "Walk-in session": "جلسة زيارة مباشرة",
  "Planned total {value}": "الإجمالي المخطط {value}",
  "Open-ended pricing": "تسعير مفتوح النهاية",
  "Food orders": "طلبات الطعام",
  "Place snack and drink orders from the live session desk view.": "أرسل طلبات الوجبات والمشروبات من عرض مكتب الجلسة المباشرة.",
  "Syncing menu": "جار مزامنة القائمة",
  "No food orders yet for this session.": "لا توجد طلبات طعام لهذه الجلسة بعد.",
  "Order {id}": "طلب {id}",
  "Created {value}": "أُنشئ {value}",
  "Preparing": "قيد التحضير",
  "Delivered": "تم التسليم",
  "Mark preparing": "اجعله قيد التحضير",
  "Cancel order": "إلغاء الطلب",
  "Mark ready": "اجعله جاهزاً",
  "Mark delivered": "اجعله مسلماً",
  "{quantity} × {name} · {price} each": "{quantity} × {name} · {price} لكل واحد",
  "Updating...": "جار التحديث...",
  "Menu item": "عنصر القائمة",
  "Quantity": "الكمية",
  "Order note": "ملاحظة الطلب",
  "No ice, send to VR room, desk pickup...": "بدون ثلج، أرسل لغرفة VR، استلام من المكتب...",
  "Placing order...": "جار إرسال الطلب...",
  "Place food order": "إرسال طلب طعام",
  "Add available food items in the Food menu section before placing orders.": "أضف عناصر طعام متاحة في قسم قائمة الطعام قبل إرسال الطلبات.",
  "Extend planned end": "تمديد النهاية المخططة",
  "Extending...": "جار التمديد...",
  "End note": "ملاحظة النهاية",
  "Reason for stopping or handoff note.": "سبب الإيقاف أو ملاحظة التسليم.",
  "Mark payment as collected before ending the session": "علّم الدفع كمحصّل قبل إنهاء الجلسة",
  "Payment method": "طريقة الدفع",
  "Cash": "نقداً",
  "Card": "بطاقة",
  "Console": "منصة ألعاب",
  "PC": "كمبيوتر",
  "VR": "واقع افتراضي",
  "Simulator": "محاكي",
  "Billiards": "بلياردو",
  "Ping pong": "تنس طاولة",
  "Room": "غرفة",
  "Transfer": "تحويل",
  "Other": "أخرى",
  "DRINKS": "مشروبات",
  "SNACKS": "سناكس",
  "MEALS": "وجبات",
  "DESSERTS": "حلويات",
  "Desk payment, card approved, split bill...": "دفع في المكتب، تمت موافقة البطاقة، فاتورة مقسمة...",
  "Stopping...": "جار الإيقاف...",
  "End and capture payment": "إنهاء وتسجيل الدفع",
  "Stop session": "إيقاف الجلسة",
  "Recent settlements": "التسويات الأخيرة",
  "Recently ended sessions stay visible here so desk staff can confirm payment and handoff notes after they leave the active floor.": "تبقى الجلسات المنتهية حديثاً ظاهرة هنا حتى يؤكد موظفو المكتب الدفع وملاحظات التسليم بعد مغادرة الصالة النشطة.",
  "{count} recent session": "{count} جلسة حديثة",
  "{count} recent sessions": "{count} جلسات حديثة",
  "No completed sessions in the current venue yet.": "لا توجد جلسات مكتملة في المكان الحالي بعد.",
  "Ended": "انتهت",
  "Payment recorded": "تم تسجيل الدفع",
  "Not recorded": "غير مسجل",
  "Duration {value}": "المدة {value}",
  "Total {value}": "الإجمالي {value}",
  "{value} overtime": "{value} زيادة",
  "Within planned duration": "ضمن المدة المخططة",
  "Planned {value}": "مخطط {value}",
  "Sign in to your workspace": "سجّل الدخول إلى مساحة عملك",
  "Password": "كلمة المرور",
  "Signing in...": "جار تسجيل الدخول...",
  "Sign in": "تسجيل الدخول",
  "Forgot password?": "نسيت كلمة المرور؟",
  "Register venue": "تسجيل مكان",
  "Unable to sign in. Please try again.": "تعذر تسجيل الدخول. حاول مرة أخرى.",
  "{appName} account recovery": "استعادة حساب {appName}",
  "Reset your password": "إعادة تعيين كلمة المرور",
  "Enter the email on your gaamed account. If it exists, we will send a reset link.": "أدخل البريد على حساب gaamed. إن كان موجوداً سنرسل رابط إعادة التعيين.",
  "Unable to request a reset link. Please try again.": "تعذر طلب رابط إعادة التعيين. حاول مرة أخرى.",
  "If an account exists for that email, a reset link is on its way.": "إذا كان هناك حساب لهذا البريد، فسيصلك رابط إعادة التعيين.",
  "Sending...": "جار الإرسال...",
  "Send reset link": "إرسال رابط إعادة التعيين",
  "Remember your password?": "تذكرت كلمة المرور؟",
  "Back to sign-in": "العودة لتسجيل الدخول",
  "Password must be at least 8 characters.": "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
  "Passwords do not match.": "كلمتا المرور غير متطابقتين.",
  "Unable to reset password. Please try again.": "تعذر إعادة تعيين كلمة المرور. حاول مرة أخرى.",
  "Choose a new password": "اختر كلمة مرور جديدة",
  "Use the reset token from the email link and set a password with at least 8 characters.": "استخدم رمز إعادة التعيين من رابط البريد وضع كلمة مرور من 8 أحرف على الأقل.",
  "Reset token": "رمز إعادة التعيين",
  "Paste token": "الصق الرمز",
  "New password": "كلمة مرور جديدة",
  "At least 8 characters": "8 أحرف على الأقل",
  "Confirm password": "تأكيد كلمة المرور",
  "Repeat password": "كرر كلمة المرور",
  "Password updated. You can sign in with the new password now.": "تم تحديث كلمة المرور. يمكنك تسجيل الدخول بكلمة المرور الجديدة الآن.",
  "Update password": "تحديث كلمة المرور",
  "Your registration needs changes before it can be approved.": "طلب تسجيلك محتاج تعديلات قبل الموافقة عليه.",
  "Read the review note and choose Edit registration to correct your details.": "اقرأ ملاحظة المراجعة واختار تعديل التسجيل عشان تصحّح بياناتك.",
  "Resubmit your updated registration for review.": "ابعت بياناتك المعدّلة للمراجعة مرة تانية.",
  "Contact Gaamed support if you need help with the requested changes.": "تواصل مع دعم Gaamed لو محتاج مساعدة في التعديلات المطلوبة.",
  "Your provider account is temporarily suspended.": "حسابك كمزوّد موقوف مؤقتاً.",
  "Bookings and venue operations are unavailable during suspension.": "الحجوزات وإدارة المكان غير متاحين أثناء الإيقاف.",
  "Read the review note for the reason and next steps.": "اقرأ ملاحظة المراجعة لمعرفة السبب والخطوات الجاية.",
  "Contact Gaamed support to resolve the suspension.": "تواصل مع دعم Gaamed لحل سبب الإيقاف.",
  "Your registration is with the Gaamed team for review.": "طلب تسجيلك مع فريق Gaamed للمراجعة.",
  "You do not need to submit another registration.": "مش محتاج تبعت طلب تسجيل جديد.",
  "Choose Refresh status to check for a decision.": "اختار تحديث الحالة عشان تعرف نتيجة المراجعة.",
  "Once approved, you can set up your venues, gaming units and team.": "بعد الموافقة، تقدر تجهّز أماكنك وأجهزة اللعب وفريقك.",
  "The service is temporarily unavailable. Please try again in a moment.": "الخدمة غير متاحة مؤقتاً. حاول مرة تانية بعد شوية.",
  "Loading your venues...": "جاري تحميل أماكنك...",
  "Load your workspace": "تحميل مساحة عملك",
  "Your workspace is ready": "مساحة عملك جاهزة",
  "Add your first venue": "أضف أول مكان ليك",
  "No venues assigned yet": "لسه مفيش أماكن مخصصة ليك",
  "Add your location and contact details. After venue approval, set up your gaming units and pricing to start taking bookings.": "أضف موقع المكان وبيانات التواصل. بعد الموافقة على المكان، جهّز أجهزة اللعب والأسعار عشان تبدأ تستقبل حجوزات.",
  "Ask your venue owner to assign you a venue, then refresh your workspace.": "اطلب من مالك المكان يخصص لك مكان، وبعدها حدّث مساحة العمل.",
  "Add venue": "إضافة مكان",
  "Refresh workspace": "تحديث مساحة العمل",
  "Load your registration": "تحميل بيانات تسجيلك",
  "Your submitted details are safe. Load them before making changes.": "بيانات تسجيلك محفوظة. حمّلها الأول قبل ما تعدّلها.",
  "Verify your email, get your application reviewed, then set up your venues.": "أكّد بريدك الإلكتروني، واستنى مراجعة طلبك، وبعدها جهّز أماكنك.",
  "Submit registration": "إرسال طلب التسجيل",
  "Incomplete information": "بيانات ناقصة",
  "Verification concern": "مشكلة في التحقق",
  "Policy violation": "مخالفة السياسة",
  "Duplicate account": "حساب مكرر",
  "Unable to load provider registration.": "تعذر تحميل تسجيل المزود.",
  "Unable to submit provider registration.": "تعذر إرسال تسجيل المزود.",
  "Resubmit registration": "إعادة إرسال التسجيل",
  "Register your venue": "سجّل مكانك",
  "Update and resend for admin review.": "حدّث البيانات وأعد إرسالها لمراجعة الإدارة.",
  "We'll review your details and set up your workspace.": "سنراجع بياناتك وننشئ مساحة عملك.",
  "Admin feedback": "ملاحظات الإدارة",
  "Business name": "اسم النشاط",
  "Downtown Arena": "ساحة وسط البلد",
  "Contact name": "اسم جهة التواصل",
  "Your name": "اسمك",
  "Phone (optional)": "الهاتف (اختياري)",
  "Application note (optional)": "ملاحظة الطلب (اختياري)",
  "Venue type, expected units, launch timeline...": "نوع المكان، الوحدات المتوقعة، موعد الإطلاق...",
  "Submitting...": "جار الإرسال...",
  "Resubmit": "إعادة الإرسال",
  "Register": "تسجيل",
  "Active venue": "المكان الحالي",
  "Added {name} to the provider team.": "اتضاف {name} لفريق المزود.",
  "Added blackout window for {name}.": "تمت إضافة فترة حظر لـ {name}.",
  "All statuses": "كل الحالات",
  "All venues": "كل الأماكن",
  "Allow partial payments": "السماح بدفعات جزئية",
  "Allow unpaid checkout": "السماح بإنهاء بدون دفع",
  "Applied": "مطبق",
  "Arabic": "العربية",
  "Balance": "المتبقي",
  "Billing address": "عنوان الفواتير",
  "Booking for this QR code is not cached yet.": "حجز رمز QR ده غير محفوظ محلياً بعد.",
  "Booked window": "فترة الحجز",
  "Booking": "حجز",
  "Building receipt preview...": "بنجهز معاينة الإيصال...",
  "Business day": "يوم التشغيل",
  "Business email": "بريد النشاط",
  "Business phone": "تليفون النشاط",
  "Business profile": "ملف النشاط",
  "Business settings saved.": "تم حفظ بيانات النشاط.",
  "Cancellation window": "مهلة الإلغاء",
  "Cap {count}": "السعة {count}",
  "Cash drawer": "درج الكاش",
  "Cash drawer closed.": "اتقفل درج الكاش.",
  "Cash drawer opened.": "اتفتح درج الكاش.",
  "Cash sales": "مبيعات الكاش",
  "Cannot start a session after the booking has already ended.": "لا يمكن بدء الجلسة بعد انتهاء الحجز.",
  "Cannot start this session more than 15 minutes before the booking begins.": "لا يمكن بدء الجلسة قبل الحجز بأكثر من 15 دقيقة.",
  "Change due": "الباقي للعميل",
  "Checkout rules": "قواعد الدفع",
  "Choose an image file.": "اختر ملف صورة.",
  "Close drawer": "قفل الدرج",
  "Closing...": "جار القفل...",
  "Collapse sidebar": "تصغير القائمة",
  "Commercial registration": "السجل التجاري",
  "Could not load customer detail.": "تعذر تحميل تفاصيل العميل.",
  "Could not load customer list.": "تعذر تحميل قائمة العملاء.",
  "Could not build receipt preview.": "تعذر تجهيز معاينة الإيصال.",
  "Could not close cash drawer.": "تعذر قفل درج الكاش.",
  "Could not issue receipt.": "تعذر إصدار الإيصال.",
  "Could not load cash drawer.": "تعذر تحميل درج الكاش.",
  "Could not read this image.": "تعذر قراءة الصورة دي.",
  "Could not load daily closeout.": "تعذر تحميل قفلة اليوم.",
  "Could not load food ordering data.": "تعذر تحميل بيانات طلبات الطعام.",
  "Could not load receipts.": "تعذر تحميل الإيصالات.",
  "Created booking for {name}.": "تم إنشاء حجز لـ {name}.",
  "Created unit {name}.": "تم إنشاء الوحدة {name}.",
  "Could not open cash drawer.": "تعذر فتح درج الكاش.",
  "Could not record payment.": "تعذر تسجيل الدفع.",
  "Could not record print.": "تعذر تسجيل الطباعة.",
  "Could not save business settings.": "تعذر حفظ إعدادات النشاط.",
  "Could not save venue finance settings.": "تعذر حفظ إعدادات مالية المكان.",
  "Counted": "المعدود",
  "Counted cash": "الكاش المعدود",
  "Currency": "العملة",
  "Customers": "العملاء",
  "Daily closeout": "قفلة اليوم",
  "Deposit %": "نسبة العربون %",
  "Deposit amount": "قيمة العربون",
  "Deposit mode": "نظام العربون",
  "Deleted unit.": "تم حذف الوحدة.",
  "Deleted unit offline. It will sync when connection returns.": "تم حذف الوحدة بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Direct upload failed before gaamed could save the image.": "فشل الرفع المباشر قبل ما Gaamed يحفظ الصورة.",
  "Drawer closeout": "قفلة الدرج",
  "Due": "مستحق",
  "English": "الإنجليزية",
  "Enter a received amount within the receipt balance.": "أدخل مبلغ مستلم لا يتجاوز المتبقي في الإيصال.",
  "Events": "الأحداث",
  "Expand sidebar": "توسيع القائمة",
  "Expected cash": "الكاش المتوقع",
  "Extended session for {name} by {minutes} minutes.": "تم تمديد جلسة {name} لمدة {minutes} دقيقة.",
  "Extended session for {name} offline. It will sync when connection returns.": "تم تمديد جلسة {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Fixed": "مبلغ ثابت",
  "Food menu": "قائمة الطعام",
  "Generated": "تم الإنشاء",
  "Issue receipt": "إصدار إيصال",
  "Image compression is not available in this browser.": "ضغط الصور غير متاح في هذا المتصفح.",
  "Image is still larger than 3 MB after compression.": "الصورة ما زالت أكبر من 3 ميجابايت بعد الضغط.",
  "Image upload failed.": "فشل رفع الصورة.",
  "Issued": "صادر",
  "Issued as {number}": "صدر برقم {number}",
  "Issuing...": "جار الإصدار...",
  "Last close": "آخر قفلة",
  "Legal name": "الاسم القانوني",
  "Line": "بند",
  "Loading closeout...": "جار تحميل قفلة اليوم...",
  "Loading policy settings...": "جار تحميل إعدادات التشغيل...",
  "Max active sessions": "أقصى عدد جلسات نشطة",
  "Max booking duration": "أقصى مدة حجز",
  "Max session duration": "أقصى مدة جلسة",
  "Marked booking for {name} as {status}.": "تم تغيير حجز {name} إلى {status}.",
  "Method": "الطريقة",
  "Min booking duration": "أقل مدة حجز",
  "New receipt": "إيصال جديد",
  "No billable lines yet.": "لا توجد بنود قابلة للفوترة بعد.",
  "No deposit": "بدون عربون",
  "No drawer": "لا يوجد درج",
  "No events recorded yet.": "لا توجد أحداث مسجلة بعد.",
  "No internal notes yet.": "لا توجد ملاحظات داخلية بعد.",
  "No payment now": "بدون دفع الآن",
  "No receipts found.": "لا توجد إيصالات.",
  "No session selected": "لا توجد جلسة محددة",
  "No sessions ready for receipt": "لا توجد جلسات جاهزة للإيصال",
  "No venues assigned": "لا توجد أماكن مخصصة",
  "No venues yet.": "لا توجد أماكن بعد.",
  "Not closed": "لم تُقفل",
  "Not counted": "لم يُعد",
  "Open": "مفتوح",
  "Open drawer": "فتح الدرج",
  "Open until {time}": "مفتوح لحد {time}",
  "Closed now · opens {time}": "مغلق الآن · يفتح {time}",
  "Opened": "اتفتح",
  "Opening cash": "كاش الافتتاح",
  "Opening...": "جار الفتح...",
  "Operations policy": "سياسة التشغيل",
  "Paid": "مدفوع",
  "Payment methods": "طرق الدفع",
  "Payment recorded for {number}.": "تم تسجيل الدفع للإيصال {number}.",
  "Payments": "المدفوعات",
  "Percent": "نسبة مئوية",
  "Provider workspace is not cached for offline availability updates.": "مساحة عمل المزود غير محفوظة محلياً لتحديثات المواعيد بدون اتصال.",
  "Provider workspace is not cached for offline unit creation.": "مساحة عمل المزود غير محفوظة محلياً لإنشاء وحدة بدون اتصال.",
  "Provider workspace is not cached for offline unit deletion.": "مساحة عمل المزود غير محفوظة محلياً لحذف وحدة بدون اتصال.",
  "Provider workspace is not cached for offline unit updates.": "مساحة عمل المزود غير محفوظة محلياً لتحديث وحدة بدون اتصال.",
  "Provider workspace is not cached for offline venue creation.": "مساحة عمل المزود غير محفوظة محلياً لإنشاء مكان بدون اتصال.",
  "Provider workspace is not cached for offline venue removal requests.": "مساحة عمل المزود غير محفوظة محلياً لطلبات إزالة الأماكن بدون اتصال.",
  "Provider workspace is not cached for offline venue updates.": "مساحة عمل المزود غير محفوظة محلياً لتحديث مكان بدون اتصال.",
  "Pick a booking or select an empty slot.": "اختر حجزاً أو حدد خانة فاضية.",
  "Print": "طباعة",
  "Print recorded for {number}.": "تم تسجيل طباعة الإيصال {number}.",
  "Prints": "مرات الطباعة",
  "QR code belongs to a booking outside the selected venue.": "رمز QR تابع لحجز خارج المكان المحدد.",
  "QR code is invalid.": "رمز QR غير صحيح.",
  "Receipt and payment actions need a live connection.": "إجراءات الإيصال والدفع تحتاج اتصالاً مباشراً.",
  "Receipt detail": "تفاصيل الإيصال",
  "Receipt footer": "تذييل الإيصال",
  "Receipt history": "سجل الإيصالات",
  "Receipt language": "لغة الإيصال",
  "Receipt prefix": "بادئة الإيصال",
  "Receipt status": "حالة الإيصال",
  "Receipt {number} issued.": "تم إصدار الإيصال {number}.",
  "Receipts": "الإيصالات",
  "Received": "المستلم",
  "Record payment": "تسجيل دفع",
  "Recording...": "جار التسجيل...",
  "Receipt": "إيصال",
  "Receipt #{number}": "إيصال رقم {number}",
  "Refresh": "تحديث",
  "Retry": "إعادة المحاولة",
  "Refunded": "مسترد",
  "Removed blackout window for {name}.": "تمت إزالة فترة الحظر من {name}.",
  "Reprint": "إعادة طباعة",
  "Require payment before ending sessions": "إلزام الدفع قبل إنهاء الجلسات",
  "Revoked {name} offline. The change will sync when connection returns.": "تم إلغاء صلاحية {name} بدون اتصال. سيتزامن التغيير عند رجوع الاتصال.",
  "Revoked {name}.": "تم إلغاء صلاحية {name}.",
  "Save business": "حفظ النشاط",
  "Save venue access": "حفظ صلاحيات الأماكن",
  "Save venue finance": "حفظ مالية المكان",
  "Saved staff venue access offline. It will sync when connection returns.": "تم حفظ صلاحيات الموظف بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Saved {name} offline. Access will sync when connection returns.": "تم حفظ {name} بدون اتصال. ستتزامن الصلاحيات عند رجوع الاتصال.",
  "Saved booking for {name} offline. It will sync when connection returns.": "تم حفظ حجز {name} بدون اتصال. سيتزامن عند رجوع الاتصال.",
  "Saved booking status for {name} offline. It will sync when connection returns.": "تم حفظ حالة حجز {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Saved availability for {name} offline. It will sync when connection returns.": "تم حفظ مواعيد {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Saved removal request offline. It will sync when connection returns.": "تم حفظ طلب الإزالة بدون اتصال. سيتزامن عند رجوع الاتصال.",
  "Saved unit {name} offline. It will sync when connection returns.": "تم حفظ الوحدة {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Saved venue request for {name} offline. It will sync when connection returns.": "تم حفظ طلب المكان {name} بدون اتصال. سيتزامن عند رجوع الاتصال.",
  "Saved venue {name} offline. It will sync when connection returns.": "تم حفظ المكان {name} بدون اتصال. سيتزامن عند رجوع الاتصال.",
  "Search number, customer, phone": "ابحث بالرقم أو العميل أو التليفون",
  "Search receipts": "بحث في الإيصالات",
  "Select a session or receipt to start.": "اختر جلسة أو إيصالاً للبدء.",
  "Select a venue": "اختر مكاناً",
  "Select a venue to configure receipt and payment settings.": "اختر مكاناً لضبط إعدادات الإيصالات والدفع.",
  "Select a venue to manage receipts.": "اختر مكاناً لإدارة الإيصالات.",
  "Select a venue to view reports.": "اختر مكاناً لعرض التقارير.",
  "Service": "الخدمة",
  "Service fee %": "نسبة رسوم الخدمة %",
  "Service fee applies to": "تُطبَّق رسوم الخدمة على",
  "VAT applies to": "تُطبَّق ضريبة القيمة المضافة على",
  "Which part of the bill VAT is charged on. Booked play time is never charged food-only VAT.": "الجزء من الفاتورة الذي تُحتسب عليه ضريبة القيمة المضافة. لا تُحتسب الضريبة الخاصة بالمأكولات على وقت اللعب المحجوز.",
  "Which part of the bill the service fee is charged on. Booked play time is never charged a food-only service fee.": "الجزء من الفاتورة الذي تُحتسب عليه رسوم الخدمة. لا تُحتسب رسوم الخدمة الخاصة بالمأكولات على وقت اللعب المحجوز.",
  "Whole bill (play time and food)": "الفاتورة كاملة (وقت اللعب والمأكولات)",
  "Service fee enabled": "تفعيل رسوم الخدمة",
  "Session": "جلسة",
  "Session ended": "انتهت الجلسة",
  "Session started": "بدأت الجلسة",
  "Settings": "الإعدادات",
  "Sent removal request for {name}. Admin approval is required.": "تم إرسال طلب إزالة {name}. موافقة الإدارة مطلوبة.",
  "Sent {name} to admin for venue approval.": "تم إرسال {name} للإدارة لاعتماد المكان.",
  "Specific": "أماكن محددة",
  "Starting...": "جار البدء...",
  "Started session for {name}.": "تم بدء جلسة {name}.",
  "Started session for {name} offline. It will sync when connection returns.": "تم بدء جلسة {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Started walk-in session for {name}.": "تم بدء جلسة زيارة مباشرة لـ {name}.",
  "Started walk-in session for {name} offline. It will sync when connection returns.": "تم بدء جلسة زيارة مباشرة لـ {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Stopped session for {name}.": "تم إيقاف جلسة {name}.",
  "Stopped session for {name} and recorded payment.": "تم إيقاف جلسة {name} وتسجيل الدفع.",
  "Stopped session for {name} offline and queued payment capture for sync.": "تم إيقاف جلسة {name} بدون اتصال وإضافة تحصيل الدفع للمزامنة.",
  "Stopped session for {name} offline. It will sync when connection returns.": "تم إيقاف جلسة {name} بدون اتصال. ستتزامن عند رجوع الاتصال.",
  "Subtotal": "الإجمالي قبل الإضافات",
  "Take payment": "تحصيل الدفع",
  "Tax registration": "التسجيل الضريبي",
  "Thanks for playing at {name}": "شكراً للعبكم في {name}",
  "This extension overlaps an existing booking on the selected unit.": "التمديد ده بيتعارض مع حجز موجود على الوحدة المحددة.",
  "This session is not cached for offline extension.": "الجلسة دي غير محفوظة محلياً للتمديد بدون اتصال.",
  "This session is not cached for offline stop.": "الجلسة دي غير محفوظة محلياً للإيقاف بدون اتصال.",
  "This booking is not cached for offline editing.": "الحجز ده غير محفوظ محلياً للتعديل بدون اتصال.",
  "This booking is not cached for offline status changes.": "الحجز ده غير محفوظ محلياً لتغيير الحالة بدون اتصال.",
  "This team member is not cached for offline updates.": "عضو الفريق ده غير محفوظ للتحديث بدون اتصال.",
  "This receipt was generated by Gaamed. For questions contact your venue directly.": "تم إنشاء هذا الإيصال بواسطة Gaamed. لأي استفسار، تواصل مع المكان مباشرة.",
  "This venue is pending admin approval. You can edit venue details, but operational actions are locked until approval.": "المكان ده في انتظار موافقة الإدارة. تقدر تعدل بياناته، لكن إجراءات التشغيل مقفولة لحد الموافقة.",
  "This venue is rejected. Operational actions are locked until an admin approves it.": "المكان ده مرفوض حالياً. إجراءات التشغيل مقفولة لحد موافقة الإدارة.",
  "Timezone unavailable": "المنطقة الزمنية غير متاحة",
  "Total": "الإجمالي",
  "Trading name": "الاسم التجاري",
  "Unable to add provider staff.": "تعذر إضافة موظف للمزود.",
  "Unable to admit booking from QR.": "تعذر إدخال الحجز من QR.",
  "Unable to create booking.": "تعذر إنشاء الحجز.",
  "Unable to create blackout window.": "تعذر إنشاء فترة الحظر.",
  "Unable to create unit.": "تعذر إنشاء الوحدة.",
  "Unable to create venue.": "تعذر إنشاء المكان.",
  "Unable to delete blackout window.": "تعذر حذف فترة الحظر.",
  "Unable to delete unit.": "تعذر حذف الوحدة.",
  "Unable to extend session.": "تعذر تمديد الجلسة.",
  "Unable to load bookings.": "تعذر تحميل الحجوزات.",
  "Unable to load provider workspace.": "تعذر تحميل مساحة عمل المزود.",
  "Unable to load live sessions.": "تعذر تحميل الجلسات المباشرة.",
  "Unable to load provider team.": "تعذر تحميل فريق المزود.",
  "Unable to revoke provider staff access.": "تعذر إلغاء صلاحية موظف المزود.",
  "Unable to start session.": "تعذر بدء الجلسة.",
  "Unable to start walk-in session.": "تعذر بدء جلسة زيارة مباشرة.",
  "Unable to stop session.": "تعذر إيقاف الجلسة.",
  "Unable to request venue removal.": "تعذر طلب إزالة المكان.",
  "Unable to update booking.": "تعذر تحديث الحجز.",
  "Unable to update booking status.": "تعذر تحديث حالة الحجز.",
  "Unable to update unit.": "تعذر تحديث الوحدة.",
  "Unable to update venue.": "تعذر تحديث المكان.",
  "Unable to update venue availability.": "تعذر تحديث مواعيد المكان.",
  "Unable to update staff venue access.": "تعذر تحديث صلاحيات أماكن الموظف.",
  "Unpaid": "غير مدفوع",
  "Updated booking for {name}.": "تم تحديث حجز {name}.",
  "Updated availability for {name}.": "تم تحديث مواعيد {name}.",
  "Updated staff venue access.": "تم تحديث صلاحيات أماكن الموظف.",
  "Updated unit {name}.": "تم تحديث الوحدة {name}.",
  "Updated venue {name}.": "تم تحديث المكان {name}.",
  "VAT": "ضريبة القيمة المضافة",
  "VIP": "مميز",
  "Variance": "الفارق",
  "Venue finance": "مالية المكان",
  "Venue finance settings saved.": "تم حفظ إعدادات مالية المكان.",
  "venue": "المكان",
  "Voided": "ملغي",
  "Walk-in": "زيارة مباشرة",
  "Walk-in guest": "ضيف زيارة مباشرة",
  "Printed {value}": "طُبع {value}",
  "Started at {value}": "بدأت في {value}",
  "Already admitted": "تم إدخاله بالفعل",
  "Ready to admit": "جاهز للدخول",
  "Confirm before admit": "أكد الحجز قبل الدخول",
  "Check-in unavailable": "تسجيل الدخول غير متاح",
  "planned end {value}": "النهاية المخططة {value}",
  "team member": "عضو فريق",
  "to": "إلى",
  "+{minutes} min overtime": "+{minutes} د زيادة",
  "{count} issued": "{count} صادر",
  "{day} is closed": "{day} مغلق",
  "{minutes} min": "{minutes} د",
  "{hours}h": "{hours} س",
  "{hours}h {minutes}m": "{hours} س {minutes} د",
  "{minutes} min so far": "{minutes} دقيقة حتى الآن",
  "{minutes} min total": "{minutes} دقيقة إجمالاً",
  "cash": "كاش",
  "card": "بطاقة",
  "transfer": "تحويل",
  "open": "مفتوح",
  "closed": "مقفول",
  "partially paid": "مدفوع جزئياً",
  "partially_paid": "مدفوع جزئياً",
  "paid": "مدفوع",
  "refunded": "مسترد",
  "receipt issued": "تم إصدار الإيصال",
  "payment recorded": "تم تسجيل الدفع",
  "print recorded": "تم تسجيل الطباعة",
  "receipt voided": "تم إلغاء الإيصال",
  "Back to workspace": "العودة إلى مساحة العمل",
  "Already registered?": "مسجل مسبقاً؟",
  "admin.dashboard.providers.title": "المزودون",
  "admin.dashboard.providers.pending": "طلبات تسجيل قيد المراجعة",
  "admin.dashboard.providers.approved": "المزودون المعتمدون",
  "admin.dashboard.providers.suspended": "المزودون الموقوفون",
  "admin.dashboard.venues.title": "الأماكن",
  "admin.dashboard.venues.pendingApprovals": "أماكن تنتظر الموافقة",
  "admin.dashboard.venues.removalRequests": "طلبات حذف قيد المراجعة",
  "admin.dashboard.venues.approved": "الأماكن المعتمدة",
  "admin.dashboard.activity.title": "النشاط",
  "admin.dashboard.activity.todayBookings": "حجوزات اليوم",
  "admin.dashboard.activity.activeSessions": "الجلسات النشطة",
  "admin.dashboard.activity.totalBookings": "إجمالي الحجوزات",
  "admin.dashboard.recentActivity": "آخر نشاط الإدارة",
  "admin.dashboard.noRecentActivity": "لا يوجد نشاط إداري مسجل بعد.",
  "admin.reviews.eyebrow": "مراجعة التقييمات",
  "admin.reviews.title": "تقييمات قيد المراجعة",
  "admin.reviews.empty": "لا توجد تقييمات قيد المراجعة.",
  "admin.reviews.loading": "جاري تحميل التقييمات...",
  "admin.reviews.error": "تعذر تحميل التقييمات قيد المراجعة.",
  "admin.reviews.approve": "موافقة",
  "admin.reviews.reject": "رفض",
  "admin.reviews.processing": "جاري المعالجة...",
  "admin.reviews.reviewNote": "ملاحظة المراجعة",
  "admin.reviews.rejectionNoteRequired": "ملاحظة المراجعة مطلوبة لرفض التقييم.",
  "admin.reviews.approvedNotice": "تمت الموافقة على التقييم.",
  "admin.reviews.rejectedNotice": "تم رفض التقييم.",
  "admin.reviews.submitted": "تم الإرسال",
  "admin.reviews.comment": "التعليق",
  "admin.reviews.noComment": "لا يوجد تعليق.",
  "admin.reports.eyebrow": "حماية المحتوى",
  "admin.reports.title": "قائمة البلاغات",
  "admin.reports.empty": "لا توجد بلاغات معلقة.",
  "admin.reports.dismiss": "رفض البلاغ",
  "admin.reports.remove": "حذف التقييم",
  "admin.reports.restrict": "تقييد صاحب الحساب",
  "admin.reports.resolved": "تمت معالجة البلاغ.",
  "admin.reports.error": "تعذر معالجة البلاغ.",
  "admin.reports.venue": "المكان",
  "admin.reports.stars": "النجوم",
  "admin.reports.comment": "تعليق الدليل",
  "admin.reports.reviewChanged": "آخر تغيير للتقييم",
  "admin.reports.rationalePlaceholder": "سبب القرار مطلوب...",
  "admin.reports.currentState": "حالة التقييم الحالية",
  "admin.reports.stale": "لقطة قديمة؛ تم تعطيل الحذف",
  "admin.reports.currentReview": "التقييم الحالي",
  "admin.reports.noCurrentReview": "لا يوجد تقييم حالي",
  "Start / End Day": "بدء / إنهاء اليوم",
  "Info": "معلومات",
  "Food / Drink": "طعام / مشروب",
  "Menu management is locked until the venue is approved.": "إدارة القائمة مقفلة لحد اعتماد المكان.",
  "Manage units from the venue list on the left, or use the unit cards below.": "أدر الوحدات من قائمة الأماكن على اليسار، أو استخدم بطاقات الوحدات أدناه.",
  "Team": "الفريق",
  "Business tabs": "تبويبات الأعمال",
  "Start Day": "بدء اليوم",
  "End Day": "إنهاء اليوم",
  "Start Day & Open Drawer": "بدء اليوم وفتح الدرج",
  "End Day & Close Drawer": "إنهاء اليوم وقفل الدرج",
  "Day started. Cash drawer is open.": "بدأ اليوم. درج الكاش مفتوح.",
  "Day ended. Cash drawer is closed.": "انتهى اليوم. درج الكاش مقفول.",
  "Starting day...": "جار بدء اليوم...",
  "Ending day...": "جار إنهاء اليوم...",
  "Could not start day.": "تعذر بدء اليوم.",
  "Could not end day.": "تعذر إنهاء اليوم.",
  "You have unsaved changes. Leaving this page will discard them.": "لديك تغييرات غير محفوظة. مغادرة هذه الصفحة ستلغيها.",
  "Select a venue first.": "اختر مكاناً أولاً.",
  "Close": "إغلاق",
  "Incoming bookings": "الحجوزات الواردة",
  "Loading...": "جار التحميل...",
  "No upcoming bookings.": "لا توجد حجوزات قادمة.",
  "Pending confirmations": "تأكيدات معلقة",
  "{count} booking awaiting confirmation": "حجز واحد بانتظار التأكيد",
  "{count} bookings awaiting confirmation": "{count} حجوزات بانتظار التأكيد",
  "Customers can't check in until these bookings are confirmed.": "لا يمكن للعملاء تسجيل الوصول حتى يتم تأكيد هذه الحجوزات.",
  "Review": "مراجعة",
  "Dismiss pending booking alert": "إغلاق تنبيه الحجوزات المعلقة",
  "{count} pending": "{count} معلق",
  "Notifications": "الإشعارات",
  "{count} unread": "{count} غير مقروء",
  "Mark all read": "تعليم الكل كمقروء",
  "Mark as read": "تعليم كمقروء",
  "No notifications yet.": "لا توجد إشعارات بعد.",
  "New bookings, sessions and food orders land here in real time.": "الحجوزات والجلسات وطلبات الطعام الجديدة تظهر هنا أول بأول.",
  "Just now": "الآن",
  "{count} min ago": "منذ {count} دقيقة",
  "{count}h ago": "منذ {count} ساعة",
  "{count}d ago": "منذ {count} يوم",
  "Open notification": "فتح الإشعار",
  "New booking from {name}": "حجز جديد من {name}",
  "Booking updated": "تم تحديث الحجز",
  "Booking updated for {name}": "تم تحديث حجز {name}",
  "Session started for {name}": "بدأت جلسة {name}",
  "Session updated": "تم تحديث الجلسة",
  "Session updated for {name}": "تم تحديث جلسة {name}",
  "Session ended for {name}": "انتهت جلسة {name}",
  "New food order": "طلب طعام جديد",
  "Food order updated": "تم تحديث طلب الطعام",
  // Platform operations console (/admin)
  "(you)": "(أنت)",
  "30 days": "30 يومًا",
  "7 days": "7 أيام",
  "90 days": "90 يومًا",
  "A password setup link was sent to {email}.": "تم إرسال رابط إعداد كلمة المرور إلى {email}.",
  "Access": "الصلاحية",
  "Access updated for {name}.": "تم تحديث صلاحية {name}.",
  "Account": "الحساب",
  "Account created, but the email was not delivered. Retry the invitation below.": "تم إنشاء الحساب، لكن لم تصل الرسالة. أعد إرسال الدعوة بالأسفل.",
  "Account deactivated": "تم إيقاف الحساب",
  "Account deleted": "تم حذف الحساب",
  "Account deleted. Retained booking and payment records have been anonymized.": "تم حذف الحساب، وأُخفيت هوية سجلات الحجز والدفع المحفوظة.",
  "Account deletion": "حذف الحساب",
  "Account deletion approved": "تمت الموافقة على حذف الحساب",
  "Account deletion rejected": "تم رفض حذف الحساب",
  "Account deletions": "طلبات حذف الحسابات",
  "Account reactivated": "تمت إعادة تفعيل الحساب",
  "Accounts": "الحسابات",
  "Action": "الإجراء",
  "Active sessions now": "الجلسات الجارية الآن",
  "Activity": "النشاط",
  "Add a note": "أضف ملاحظة",
  "Add a note and pick a reason before rejecting or suspending.": "أضف ملاحظة واختر سببًا قبل الرفض أو الإيقاف.",
  "Add a note and pick a reason before rejecting.": "أضف ملاحظة واختر سببًا قبل الرفض.",
  "Add a provider": "إضافة شريك",
  "Add provider": "إضافة شريك",
  "Add rule": "إضافة قاعدة",
  "Added": "تاريخ الإضافة",
  "Admin console": "لوحة الإدارة",
  "Admin note:": "ملاحظة الإدارة:",
  "Admin note: {note}": "ملاحظة الإدارة: {note}",
  "Admin review note...": "ملاحظة مراجعة الإدارة...",
  "Admin sections": "أقسام الإدارة",
  "Admin started assisting the owner": "بدأ مسؤول مساعدة المالك",
  "Admin stopped assisting": "أنهى المسؤول المساعدة",
  "Admins": "المسؤولون",
  "Admins manage accounts, commission, assistance and this team. Support can review queues, help with account access and add notes.": "يدير المسؤولون الحسابات والعمولات والمساعدة وهذا الفريق. ويستطيع الدعم مراجعة القوائم والمساعدة في الدخول للحسابات وإضافة الملاحظات.",
  "Admins only": "للمسؤولين فقط",
  "All queues are clear.": "لا يوجد شيء في الانتظار.",
  "All-time bookings": "إجمالي الحجوزات",
  "Amount (EGP)": "المبلغ (ج.م)",
  "An admin note is required to reject a deletion request.": "يلزم كتابة ملاحظة لرفض طلب الحذف.",
  "Analytics": "التحليلات",
  "Application note: {note}": "ملاحظة الطلب: {note}",
  "Approval queues": "قوائم الموافقات",
  "Approvals": "الموافقات",
  "Approve": "موافقة",
  "Approve removal": "الموافقة على الإزالة",
  "Approve selected": "الموافقة على المحدد",
  "Approved": "معتمد",
  "Approved providers": "الشركاء المعتمدون",
  "Assist the owner": "مساعدة المالك",
  "At least one bookable unit": "وحدة واحدة على الأقل متاحة للحجز",
  "At least {count} characters.": "{count} أحرف على الأقل.",
  "Audit log": "سجل التدقيق",
  "Awaiting password setup": "في انتظار إعداد كلمة المرور",
  "Back to Home": "العودة للرئيسية",
  "Balance owed to gaamed": "المستحق لـ gaamed",
  "Bank transfer": "تحويل بنكي",
  "Booking funnel": "مسار الحجوزات",
  "Bookings today": "حجوزات اليوم",
  "Bookings, sessions, food orders and payments are handled by the venue. Point the player to the venue's front desk or phone.": "الحجوزات والجلسات وطلبات الطعام والمدفوعات مسؤولية المكان. وجّه اللاعب إلى استقبال المكان أو رقمه.",
  "Both together": "الاثنان معًا",
  "Busiest providers": "أكثر الشركاء حجزًا",
  "Busiest venues": "أكثر الأماكن حجزًا",
  "Business details": "بيانات النشاط",
  "Business details edited by the venue": "عدّل المكان بيانات النشاط",
  "Cancellations": "الإلغاءات",
  "Cash drawer closed": "تم إغلاق درج النقدية",
  "Cash drawer opened": "تم فتح درج النقدية",
  "Change access for {name}?": "تغيير صلاحية {name}؟",
  "Change made while assisting": "تغيير أثناء المساعدة",
  "Charged on": "تُحسب على",
  "Close editor": "إغلاق التعديل",
  "Close menu": "إغلاق القائمة",
  "Commission": "العمولة",
  "Commission for this month": "عمولة هذا الشهر",
  "Commission rule added": "تمت إضافة قاعدة عمولة",
  "Commission rule ended": "تم إنهاء قاعدة عمولة",
  "Commission terms": "شروط العمولة",
  "Complete": "مكتمل",
  "Completed {date}": "اكتمل {date}",
  "Counts towards the overall balance.": "تُحسب ضمن الرصيد الإجمالي.",
  "Create provider": "إنشاء الشريك",
  "Dark": "داكن",
  "Deactivate": "إيقاف",
  "Deactivate this account?": "إيقاف هذا الحساب؟",
  "Deactivated": "موقوف",
  "Decide who goes live. Every decision is recorded with your name.": "قرر من يظهر على المنصة. يُسجل كل قرار باسمك.",
  "Decided this session ({count})": "تم البت فيها في هذه الجلسة ({count})",
  "Decision": "القرار",
  "Decision reason for {name}": "سبب القرار بشأن {name}",
  "Decision saved.": "تم حفظ القرار.",
  "Delete account": "حذف الحساب",
  "Delete account…": "حذف الحساب…",
  "Delete this customer account?": "حذف حساب هذا العميل؟",
  "Deleted account": "حساب محذوف",
  "Deletion requests": "طلبات الحذف",
  "Dismiss error message": "إخفاء رسالة الخطأ",
  "Dismiss message": "إخفاء الرسالة",
  "Dismiss success message": "إخفاء رسالة النجاح",
  "Dismissed": "مرفوض",
  "Due date (optional)": "تاريخ الاستحقاق (اختياري)",
  "Edit profile": "تعديل البيانات",
  "Email delivery failed. You can retry the invitation.": "تعذر إرسال الرسالة. يمكنك إعادة إرسال الدعوة.",
  "Email not verified": "البريد غير مؤكد",
  "Email the owner a password setup link now": "أرسل للمالك رابط إعداد كلمة المرور الآن",
  "Email verified": "البريد مؤكد",
  "Email, name or phone": "البريد أو الاسم أو الهاتف",
  "End date": "تاريخ الانتهاء",
  "End rule": "إنهاء القاعدة",
  "Ends (optional)": "تنتهي (اختياري)",
  "Ends {date}": "تنتهي {date}",
  "Every business on gaamed, with its venues, team and settlement.": "كل نشاط على gaamed بأماكنه وفريقه وتسويته.",
  "Every physical venue, whether it's live, and its commission terms.": "كل مكان فعلي، وهل هو ظاهر، وشروط عمولته.",
  "Every staff decision and sensitive venue change, newest first.": "كل قرارات الفريق والتغييرات الحساسة في الأماكن، الأحدث أولًا.",
  "Explain the decision. The owner sees this.": "اشرح القرار. سيراه المالك.",
  "Filter by name, city, email or phone": "تصفية بالاسم أو المدينة أو البريد أو الهاتف",
  "Filter by venue, city or provider": "تصفية بالمكان أو المدينة أو الشريك",
  "Filter providers": "تصفية الشركاء",
  "Filter venues": "تصفية الأماكن",
  "Find accounts": "البحث عن حسابات",
  "Food and drinks charged by venues": "ما حصّلته الأماكن مقابل الطعام والمشروبات",
  "Food and drinks only": "الطعام والمشروبات فقط",
  "Fraud risk": "اشتباه احتيال",
  "From": "من",
  "Full name": "الاسم الكامل",
  "Good to see you, {name}": "أهلًا {name}",
  "Home": "الرئيسية",
  "How bookings progressed in the selected period.": "كيف تقدمت الحجوزات في الفترة المحددة.",
  "ID {id}": "المعرّف {id}",
  "Inactive": "غير نشط",
  "InstaPay": "إنستاباي",
  "Internal application note": "ملاحظة داخلية على الطلب",
  "Internal to the gaamed team. Venues and players never see these.": "داخلية لفريق gaamed. لا يراها المكان ولا اللاعبون.",
  "Invitation sent to {email}.": "تم إرسال الدعوة إلى {email}.",
  "Invitation sent to {email}. The link lasts seven days.": "تم إرسال الدعوة إلى {email}. الرابط صالح لسبعة أيام.",
  "Invite a teammate": "دعوة عضو للفريق",
  "Invite the owner to their workspace?": "دعوة المالك إلى مساحة عمله؟",
  "Issue statement for {amount}": "إصدار كشف بقيمة {amount}",
  "Issued statements past their due date": "كشوف صادرة تجاوزت تاريخ الاستحقاق",
  "Issued {date}. These figures are fixed; later changes don't affect them.": "صدر في {date}. هذه الأرقام ثابتة ولا تتأثر بأي تغييرات لاحقة.",
  "It stops counting towards the balance. Payments recorded against it stay on the ledger.": "لن يُحسب ضمن الرصيد. تبقى المدفوعات المسجلة عليه في الدفتر.",
  "It stops showing on the venue page. The author isn't notified.": "سيختفي من صفحة المكان. لن يتم إخطار الكاتب.",
  "Joined": "تاريخ الانضمام",
  "Keep request pending": "إبقاء الطلب معلقًا",
  "Keep the venue": "إبقاء المكان",
  "Last decision": "آخر قرار",
  "Last note: {note}": "آخر ملاحظة: {note}",
  "Last payment": "آخر دفعة",
  "Last sign-in": "آخر تسجيل دخول",
  "Light": "فاتح",
  "Linked to this month's statement.": "مرتبطة بكشف هذا الشهر.",
  "Live preview from completed sessions. Issue a statement once the month is over to fix the amount.": "معاينة مباشرة من الجلسات المكتملة. أصدر الكشف بعد انتهاء الشهر لتثبيت المبلغ.",
  "Live venues": "الأماكن الظاهرة",
  "Live venues without commission terms": "أماكن ظاهرة بلا شروط عمولة",
  "Load older activity": "تحميل نشاط أقدم",
  "Load older events": "تحميل أحداث أقدم",
  "Loading requests...": "جار تحميل الطلبات...",
  "Loading team…": "جار تحميل الفريق…",
  "Manage it on the Team page.": "أدِره من صفحة الفريق.",
  "Missing": "غير موجود",
  "Missing: {items}": "ناقص: {items}",
  "Mobile wallet": "محفظة إلكترونية",
  "Moderation": "الإشراف",
  "Moderation queues": "قوائم الإشراف",
  "Moderator note: {note}": "ملاحظة المشرف: {note}",
  "Month": "الشهر",
  "Needs action": "يحتاج إجراء",
  "Never": "لم يحدث",
  "New businesses waiting for a decision": "أنشطة جديدة تنتظر قرارًا",
  "New venues": "أماكن جديدة",
  "No": "لا",
  "No account deletion requests match the selected filter.": "لا توجد طلبات حذف تطابق التصفية.",
  "No accounts match.": "لا توجد حسابات مطابقة.",
  "No approved providers yet.": "لا يوجد شركاء معتمدون بعد.",
  "No comment": "بلا تعليق",
  "No commission terms": "بلا شروط عمولة",
  "No commission terms at: {venues}": "لا توجد شروط عمولة في: {venues}",
  "No commission terms at: {venues}. Set them on each venue's page.": "لا توجد شروط عمولة في: {venues}. حددها من صفحة كل مكان.",
  "No events match these filters.": "لا توجد أحداث تطابق التصفية.",
  "No map pin yet. The owner must add one before approval.": "لا يوجد موقع على الخريطة بعد. يجب أن يضيفه المالك قبل الموافقة.",
  "No new venues are waiting.": "لا توجد أماكن جديدة في الانتظار.",
  "No notes yet.": "لا توجد ملاحظات بعد.",
  "No payments recorded yet.": "لا توجد مدفوعات مسجلة بعد.",
  "No phone": "لا يوجد هاتف",
  "No provider registrations are waiting.": "لا توجد طلبات تسجيل شركاء في الانتظار.",
  "No providers match this filter.": "لا يوجد شركاء يطابقون التصفية.",
  "No providers match.": "لا يوجد شركاء مطابقون.",
  "No published reviews": "لا توجد تقييمات منشورة",
  "No recorded activity yet.": "لا يوجد نشاط مسجل بعد.",
  "No removal requests are waiting.": "لا توجد طلبات إزالة في الانتظار.",
  "No reviews written.": "لم يكتب أي تقييم.",
  "No reviews yet.": "لا توجد تقييمات بعد.",
  "No rule covered these dates": "لا توجد قاعدة تغطي هذه التواريخ",
  "No statements issued yet.": "لم يصدر أي كشف بعد.",
  "No terms set": "لم تُحدد شروط",
  "No terms today. Sessions here earn gaamed nothing.": "لا توجد شروط سارية اليوم. جلسات هذا المكان لا تحقق أي عمولة لـ gaamed.",
  "No venues match.": "لا توجد أماكن مطابقة.",
  "No-shows": "عدم الحضور",
  "None yet": "لا يوجد بعد",
  "Not issued": "لم يصدر",
  "Not set": "غير محدد",
  "Not visible in the app": "غير ظاهر في التطبيق",
  "Note (optional)": "ملاحظة (اختياري)",
  "Note to the owner": "ملاحظة للمالك",
  "Note to the owner: {note}": "ملاحظة للمالك: {note}",
  "Note to the provider": "ملاحظة للشريك",
  "Note to the provider: {note}": "ملاحظة للشريك: {note}",
  "Note: {note}": "ملاحظة: {note}",
  "Nothing blocks sign-in. If they still can't get in, send a password reset link.": "لا يوجد ما يمنع الدخول. إن استمرت المشكلة أرسل رابط إعادة تعيين كلمة المرور.",
  "Nothing here yet.": "لا يوجد شيء هنا بعد.",
  "Older activity could not be loaded.": "تعذر تحميل النشاط الأقدم.",
  "Older events could not be loaded.": "تعذر تحميل الأحداث الأقدم.",
  "Oldest waiting {age}": "الأقدم ينتظر منذ {age}",
  "Only admins can approve a deletion. The note box above is sent only if you reject. To hand this to an admin, leave a note on the player's account.": "الموافقة على الحذف للمسؤولين فقط. تُرسل الملاحظة أعلاه فقط عند الرفض. لتسليم الطلب لمسؤول، اترك ملاحظة في حساب اللاعب.",
  "Open account": "فتح الحساب",
  "Only admins can change commission terms.": "تعديل شروط العمولة للمسؤولين فقط.",
  "Only admins can reject or suspend a provider.": "رفض الشريك أو إيقافه للمسؤولين فقط.",
  "Only admins can suspend a provider.": "إيقاف الشريك للمسؤولين فقط.",
  "Open audit log": "فتح سجل التدقيق",
  "Open deletion queue": "فتح قائمة الحذف",
  "Open menu": "فتح القائمة",
  "Open provider record": "فتح سجل الشريك",
  "Open settlement": "فتح التسوية",
  "Operations console": "لوحة العمليات",
  "Overdue": "متأخر",
  "Owed to gaamed (issued, unpaid)": "المستحق لـ gaamed (صادر وغير مدفوع)",
  "Owner account created": "تم إنشاء حساب المالك",
  "Owner email": "بريد المالك",
  "Owner setup": "إعداد حساب المالك",
  "Owner setup link sent": "تم إرسال رابط إعداد المالك",
  "Owner's note: {note}": "ملاحظة المالك: {note}",
  "Owners asking to remove a venue": "ملاك يطلبون إزالة مكان",
  "Page not found": "الصفحة غير موجودة",
  "Paid on": "تاريخ الدفع",
  "Paid to date": "المدفوع حتى الآن",
  "Paid {paid} of {total}": "تم دفع {paid} من {total}",
  "Password reset link sent": "تم إرسال رابط إعادة تعيين كلمة المرور",
  "Payment recorded.": "تم تسجيل الدفعة.",
  "Payments received": "المدفوعات المستلمة",
  "Period": "الفترة",
  "Photos or cover image": "صور أو صورة غلاف",
  "Platform policies edited": "تم تعديل سياسات المنصة",
  "Platform team": "فريق المنصة",
  "Platform-wide numbers. Venue-level detail lives in each provider's workspace.": "أرقام على مستوى المنصة. تفاصيل كل مكان في مساحة عمل الشريك.",
  "Player": "لاعب",
  "Player reviews waiting to be published": "تقييمات لاعبين تنتظر النشر",
  "Players": "اللاعبون",
  "Players asking gaamed to delete their account. Deleting is permanent and anonymizes their retained records.": "لاعبون يطلبون حذف حساباتهم من gaamed. الحذف نهائي ويخفي هوية سجلاتهم المحفوظة.",
  "Players asking to delete their account": "لاعبون يطلبون حذف حساباتهم",
  "Players pay venues directly. Each provider owes gaamed its commission, which you settle here.": "يدفع اللاعبون للأماكن مباشرة. كل شريك مدين لـ gaamed بعمولته، وتتم تسويتها هنا.",
  "Players, venue owners and staff, and the gaamed team.": "اللاعبون وملاك الأماكن وموظفوها وفريق gaamed.",
  "Prepare the workspace before inviting the owner. Admins never see or set their password.": "جهّز مساحة العمل قبل دعوة المالك. لا يرى المسؤولون كلمة مروره ولا يحددونها.",
  "Processing...": "جار المعالجة...",
  "Profile saved.": "تم حفظ البيانات.",
  "Promo code created": "تم إنشاء كود خصم",
  "Promo code edited": "تم تعديل كود خصم",
  "Provider": "الشريك",
  "Provider account created": "تم إنشاء حساب الشريك",
  "Provider approved": "الشريك معتمد",
  "Provider profile edited": "تم تعديل بيانات الشريك",
  "Provider registrations": "طلبات تسجيل الشركاء",
  "Provider status": "حالة الشريك",
  "Provider status changed": "تغيرت حالة الشريك",
  "Providers": "الشركاء",
  "Providers with overdue commission": "شركاء متأخرون في سداد العمولة",
  "Publish or reject player reviews, and resolve reports about reviews and their authors.": "انشر تقييمات اللاعبين أو ارفضها، وتعامل مع البلاغات عن التقييمات وكتّابها.",
  "Published review removed": "تمت إزالة تقييم منشور",
  "Put {name} back on gaamed?": "إعادة {name} إلى gaamed؟",
  "Rating": "التقييم",
  "Reactivate": "إعادة التفعيل",
  "Reactivate this account?": "إعادة تفعيل هذا الحساب؟",
  "Reason (kept in the audit log)": "السبب (يُحفظ في سجل التدقيق)",
  "Reason (needed to reject or suspend)": "السبب (مطلوب للرفض أو الإيقاف)",
  "Reason (shown to the owner and kept in the audit log)": "السبب (يظهر للمالك ويُحفظ في سجل التدقيق)",
  "Reason:": "السبب:",
  "Receipt issued": "تم إصدار إيصال",
  "Receipt payment recorded": "تم تسجيل دفعة على إيصال",
  "Receipt printed": "تمت طباعة إيصال",
  "Receipt refunded": "تم رد مبلغ إيصال",
  "Receipt voided": "تم إلغاء إيصال",
  "Recent activity": "آخر النشاط",
  "Record a payment": "تسجيل دفعة",
  "Record money the provider sent to gaamed.": "سجّل المبالغ التي أرسلها الشريك إلى gaamed.",
  "Record type": "نوع السجل",
  "Records": "السجلات",
  "Reference (optional)": "المرجع (اختياري)",
  "Refresh team": "تحديث الفريق",
  "Registered {date}": "سُجل في {date}",
  "Reject selected": "رفض المحدد",
  "Removal reason: {note}": "سبب الإزالة: {note}",
  "Removal request": "طلب إزالة",
  "Removal requested": "مطلوب إزالته",
  "Remove review": "إزالة التقييم",
  "Remove this published review?": "إزالة هذا التقييم المنشور؟",
  "Removed": "تمت إزالته",
  "Report resolved": "تم التعامل مع البلاغ",
  "Reports": "البلاغات",
  "Reports filed: {filed} · reports about them: {against}": "بلاغات قدمها: {filed} · بلاغات ضده: {against}",
  "Request rejected.": "تم رفض الطلب.",
  "Requested {date}": "طُلب في {date}",
  "Resend invitation": "إعادة إرسال الدعوة",
  "Resend setup link": "إعادة إرسال رابط الإعداد",
  "Resend the owner's setup link?": "إعادة إرسال رابط إعداد المالك؟",
  "Resend verification code": "إعادة إرسال رمز التحقق",
  "Restore access": "استعادة الوصول",
  "Restore venue": "استعادة المكان",
  "Retry loading requests": "إعادة تحميل الطلبات",
  "Review moderated": "تمت مراجعة تقييم",
  "Review note for {email}": "ملاحظة المراجعة لـ {email}",
  "Review note for {name}": "ملاحظة المراجعة لـ {name}",
  "Reviewed {date}": "رُوجع في {date}",
  "Reviews or authors reported by players": "تقييمات أو كتّاب أبلغ عنهم اللاعبون",
  "Reviews to moderate": "تقييمات للمراجعة",
  "Role for {email}": "صلاحية {email}",
  "Rule": "القاعدة",
  "Save note": "حفظ الملاحظة",
  "Save profile": "حفظ البيانات",
  "Search": "بحث",
  "Search by business or venue name, email, phone, or an ID.": "ابحث باسم النشاط أو المكان أو البريد أو الهاتف أو المعرّف.",
  "Search providers, venues and accounts": "ابحث في الشركاء والأماكن والحسابات",
  "Search providers, venues, emails or phones": "ابحث عن شريك أو مكان أو بريد أو هاتف",
  "Select a reason": "اختر سببًا",
  "Select all": "تحديد الكل",
  "Select {name}": "تحديد {name}",
  "Send invitation": "إرسال الدعوة",
  "Send password reset link": "إرسال رابط إعادة تعيين كلمة المرور",
  "Send setup link": "إرسال رابط الإعداد",
  "Send {email} a fresh 7-day password setup link. Make sure the venues are ready first.": "أرسل إلى {email} رابطًا جديدًا لإعداد كلمة المرور صالحًا 7 أيام. تأكد أولًا من جاهزية الأماكن.",
  "Session time and food": "وقت الجلسة والطعام",
  "Session time charged by venues": "ما حصّلته الأماكن مقابل وقت اللعب",
  "Session time only": "وقت الجلسة فقط",
  "Sessions there earn gaamed nothing until terms are set": "لا تحقق جلساتها عمولة لـ gaamed حتى تُحدد الشروط",
  "Set": "محددة",
  "Settlement": "التسوية",
  "Settlement payment recorded": "تم تسجيل دفعة تسوية",
  "Settlement statement issued": "تم إصدار كشف تسوية",
  "Settlement statement voided": "تم إلغاء كشف تسوية",
  "Settlements": "التسويات",
  "Setup complete": "الإعداد مكتمل",
  "Setup pending": "الإعداد معلق",
  "Shared note (needed to reject)": "ملاحظة مشتركة (مطلوبة للرفض)",
  "Shared note for the selected providers": "ملاحظة مشتركة للشركاء المحددين",
  "Shared reason": "سبب مشترك",
  "Sign out": "تسجيل الخروج",
  "Sign out everywhere": "تسجيل الخروج من كل الأجهزة",
  "Sign {name} out of every device?": "تسجيل خروج {name} من كل الأجهزة؟",
  "Sign-in": "تسجيل الدخول",
  "Signed out of all devices": "تم تسجيل الخروج من كل الأجهزة",
  "Signed-in devices": "الأجهزة المسجل منها",
  "Staff member": "عضو فريق",
  "Statement": "الكشف",
  "Statement issued. Its amounts are now fixed.": "صدر الكشف وأصبحت مبالغه ثابتة.",
  "Statement voided.": "تم إلغاء الكشف.",
  "Statements": "الكشوف",
  "Status filter": "تصفية الحالة",
  "Status updated.": "تم تحديث الحالة.",
  "Summary": "الملخص",
  "Suspend": "إيقاف",
  "Suspended": "موقوف",
  "Suspended providers": "الشركاء الموقوفون",
  "Switch to Arabic": "التبديل إلى العربية",
  "Switch to English": "التبديل إلى الإنجليزية",
  "Switch to dark mode": "التبديل إلى الوضع الداكن",
  "Switch to light mode": "التبديل إلى الوضع الفاتح",
  "System": "النظام",
  "Take down": "إزالة من المنصة",
  "Take {name} off gaamed?": "إزالة {name} من gaamed؟",
  "Teammate access changed": "تغيرت صلاحية عضو الفريق",
  "Teammate invitation resent": "أعيد إرسال دعوة عضو الفريق",
  "Teammate invited": "تمت دعوة عضو للفريق",
  "The account is deactivated, so sign-in is refused.": "الحساب موقوف، لذلك يُرفض تسجيل الدخول.",
  "The account was not updated.": "لم يتم تحديث الحساب.",
  "The code was not sent.": "لم يتم إرسال الرمز.",
  "The decision was not saved. Your note is kept; try again.": "لم يُحفظ القرار. ملاحظتك محفوظة، حاول مرة أخرى.",
  "The note was not saved. Your text is still here; try again.": "لم تُحفظ الملاحظة. نصك ما زال موجودًا، حاول مرة أخرى.",
  "The owner's email can't be changed here.": "لا يمكن تغيير بريد المالك من هنا.",
  "The payment was not recorded.": "لم تُسجل الدفعة.",
  "The profile was not saved.": "لم تُحفظ البيانات.",
  "The provider was not created.": "لم يتم إنشاء الشريك.",
  "The reset link was not sent.": "لم يتم إرسال رابط إعادة التعيين.",
  "The review was not removed.": "لم تتم إزالة التقييم.",
  "The rule was not ended.": "لم يتم إنهاء القاعدة.",
  "The rule was not saved.": "لم تُحفظ القاعدة.",
  "The selected providers were not updated.": "لم يتم تحديث الشركاء المحددين.",
  "The selected venues were not updated.": "لم يتم تحديث الأماكن المحددة.",
  "The setup email was not delivered. The owner stays locked; you can safely retry.": "لم تصل رسالة الإعداد. يظل حساب المالك مقفلًا، ويمكنك إعادة المحاولة بأمان.",
  "The setup link was not sent.": "لم يتم إرسال رابط الإعداد.",
  "The statement was not issued.": "لم يصدر الكشف.",
  "The statement was not voided.": "لم يتم إلغاء الكشف.",
  "The venue becomes live again if its provider is approved and it has a bookable unit.": "سيظهر المكان مجددًا إذا كان الشريك معتمدًا وبه وحدة متاحة للحجز.",
  "The venue disappears from the app and can't take new bookings. The owner is notified. The provider's other venues aren't affected.": "سيختفي المكان من التطبيق ولن يستقبل حجوزات جديدة. سيتم إخطار المالك. لن تتأثر أماكن الشريك الأخرى.",
  "The venue was not restored.": "لم تتم استعادة المكان.",
  "The venue was not taken down.": "لم تتم إزالة المكان.",
  "Their email isn't verified. Sign-in asks for the verification code first.": "البريد غير مؤكد. يطلب تسجيل الدخول رمز التحقق أولًا.",
  "They are signed out everywhere and can't sign in until an admin reactivates them.": "سيتم تسجيل خروجه من كل الأجهزة ولن يستطيع الدخول حتى يعيد مسؤول تفعيله.",
  "They can sign in again with their existing password.": "يمكنه الدخول مجددًا بكلمة المرور الحالية.",
  "They can sign in, but the {provider} workspace is {status}.": "يمكنه تسجيل الدخول، لكن حالة مساحة عمل {provider}: {status}.",
  "They choose their own password. No access is available until setup is complete.": "يختار كلمة المرور بنفسه. لا يوجد وصول قبل اكتمال الإعداد.",
  "They haven't set a password yet. They need the setup link.": "لم يحدد كلمة مرور بعد. يحتاج رابط الإعداد.",
  "They were not signed out.": "لم يتم تسجيل خروجه.",
  "This console page doesn't exist.": "هذه الصفحة غير موجودة في اللوحة.",
  "This is a gaamed team account.": "هذا حساب من فريق gaamed.",
  "This month": "هذا الشهر",
  "This month so far": "هذا الشهر حتى الآن",
  "This page could not be loaded. Check your connection and try again.": "تعذر تحميل هذه الصفحة. تحقق من الاتصال وحاول مرة أخرى.",
  "This page is limited to admin accounts. Ask an admin if you need something here.": "هذه الصفحة للمسؤولين فقط. اطلب من مسؤول إن احتجت شيئًا منها.",
  "This permanently deletes {email} and anonymizes their retained booking and payment records. This cannot be undone.": "سيؤدي هذا إلى حذف {email} نهائيًا وإخفاء هوية سجلات الحجز والدفع المحفوظة. لا يمكن التراجع.",
  "This signs {email} out, locks the owner account and emails a new 7-day setup link.": "سيؤدي هذا إلى تسجيل خروج {email} وقفل حساب المالك وإرسال رابط إعداد جديد صالح 7 أيام.",
  "To": "إلى",
  "To block a venue owner, suspend their provider instead.": "لمنع مالك مكان، أوقف الشريك بدلًا من ذلك.",
  "Tournament created": "تم إنشاء بطولة",
  "Tournament edited": "تم تعديل بطولة",
  "Trends": "الاتجاهات",
  "Type": "النوع",
  "Type at least 2 characters in the search box.": "اكتب حرفين على الأقل في مربع البحث.",
  "Unable to delete the account. Please try again.": "تعذر حذف الحساب. حاول مرة أخرى.",
  "Unable to load account deletion requests.": "تعذر تحميل طلبات حذف الحسابات.",
  "Unable to load the team.": "تعذر تحميل الفريق.",
  "Unable to reject deletion request.": "تعذر رفض طلب الحذف.",
  "Unable to update team access.": "تعذر تحديث صلاحيات الفريق.",
  "Upcoming closures": "إغلاقات قادمة",
  "Update access": "تحديث الصلاحية",
  "Updated {count} providers.": "تم تحديث {count} شريك.",
  "Updated {count} venues.": "تم تحديث {count} مكان.",
  "Use this for a lost phone or a shared device. Their password doesn't change.": "استخدم هذا عند فقدان الهاتف أو في جهاز مشترك. لن تتغير كلمة المرور.",
  "Venue active": "المكان مفعّل",
  "Venue approved": "المكان معتمد",
  "Venue business": "نشاط المكان",
  "Venue decision": "قرار بشأن مكان",
  "Venue issues": "مشكلات المكان",
  "Venue owner": "مالك مكان",
  "Venue owners": "ملاك الأماكن",
  "Venue receipt settings edited": "تم تعديل إعدادات إيصالات المكان",
  "Venue removals": "إزالة الأماكن",
  "Venue restored": "تمت استعادة المكان",
  "Venue restored.": "تمت استعادة المكان.",
  "Venue staff": "موظف مكان",
  "Venue taken down": "تمت إزالة المكان",
  "Venue taken down.": "تمت إزالة المكان.",
  "Venues waiting to go live": "أماكن تنتظر الظهور",
  "Verification code sent": "تم إرسال رمز التحقق",
  "Visible in the app": "ظاهر في التطبيق",
  "Void": "إلغاء",
  "Void statement": "إلغاء الكشف",
  "Void this statement?": "إلغاء هذا الكشف؟",
  "Waiting for password setup": "في انتظار إعداد كلمة المرور",
  "What gaamed takes from this venue. Combine rules, e.g. 15% of session time plus 5% of food.": "ما تحصل عليه gaamed من هذا المكان. يمكن الجمع بين القواعد، مثل 15% من وقت الجلسة و5% من الطعام.",
  "What happened, what was agreed, what to follow up on": "ما حدث، وما تم الاتفاق عليه، وما يجب متابعته",
  "What the app checks before showing this venue to players.": "ما يتحقق منه التطبيق قبل عرض المكان للاعبين.",
  "What the venue charges for food orders": "ما يحصّله المكان مقابل طلبات الطعام",
  "What the venue charges for play time": "ما يحصّله المكان مقابل وقت اللعب",
  "Who can use this console, and what they can do.": "من يمكنه استخدام هذه اللوحة، وماذا يمكنه أن يفعل.",
  "Why is it (not) visible?": "لماذا يظهر أو لا يظهر؟",
  "Why it breaks the review rules": "لماذا يخالف قواعد التقييم",
  "Why it's safe to restore": "لماذا الاستعادة آمنة",
  "Work": "العمل",
  "Work in this owner's real workspace for up to 60 minutes. Every change is recorded under your name.": "اعمل في مساحة عمل المالك الفعلية لمدة تصل إلى 60 دقيقة. يُسجل كل تغيير باسمك.",
  "Yes": "نعم",
  "You can issue this statement after the month ends.": "يمكنك إصدار هذا الكشف بعد انتهاء الشهر.",
  "Your review notes are saved on this page.": "ملاحظاتك محفوظة في هذه الصفحة.",
  "account deletion request": "طلب حذف حساب",
  "assistance": "مساعدة",
  "due {date}": "يستحق {date}",
  "e.g. launch discount agreed with the owner": "مثال: خصم إطلاق متفق عليه مع المالك",
  "food and drinks": "الطعام والمشروبات",
  "missing": "ناقص",
  "no end date": "بلا تاريخ انتهاء",
  "of {base}": "من {base}",
  "passed": "مستوفى",
  "provider": "شريك",
  "receipt": "إيصال",
  "session time": "وقت الجلسة",
  "session time and food": "وقت الجلسة والطعام",
  "team": "الفريق",
  "tournament": "بطولة",
  "ugc review report": "بلاغ عن تقييم",
  "user": "مستخدم",
  "venue review": "تقييم مكان",
  "{active} live of {total}": "{active} ظاهر من {total}",
  "{amount} EGP by {method}": "{amount} ج.م عبر {method}",
  "{amount} EGP for {start} to {end}": "{amount} ج.م عن الفترة من {start} إلى {end}",
  "{count} items are waiting for the team.": "{count} عنصر ينتظر الفريق.",
  "{count} live venues without terms": "{count} مكان ظاهر بلا شروط",
  "{count} matches for “{query}”": "{count} نتيجة لـ “{query}”",
  "{count} review": "{count} تقييم",
  "{count} reviews": "{count} تقييمات",
  "{count} sessions had no matching rule and earned no commission.": "{count} جلسة بلا قاعدة مطابقة ولم تحقق عمولة.",
  "{count} waiting": "{count} في الانتظار",
  "{email} will have {role} access. Existing sessions will be signed out.": "سيحصل {email} على صلاحية {role}. سيتم تسجيل الخروج من الجلسات الحالية.",
  "{email} will lose access. Existing sessions will be signed out.": "سيفقد {email} الوصول. سيتم تسجيل الخروج من الجلسات الحالية.",
  "{from} → {to}": "{from} ← {to}",
  "{message} Email delivery failed; try again later.": "{message} تعذر إرسال البريد، حاول لاحقًا.",
  "{name} added. A 7-day password setup link was sent to {email}.": "تمت إضافة {name}. أُرسل رابط إعداد كلمة المرور صالح 7 أيام إلى {email}.",
  "{name} added. Prepare the workspace, then send the owner a setup link.": "تمت إضافة {name}. جهّز مساحة العمل ثم أرسل للمالك رابط الإعداد.",
  "{name} is now {status}.": "حالة {name} الآن: {status}.",
  "{name} was created, but the setup email failed. Use Send setup link on the record to retry.": "تم إنشاء {name} لكن فشل إرسال رسالة الإعداد. استخدم زر إرسال رابط الإعداد في السجل لإعادة المحاولة.",
  "{percent}% of {base}": "{percent}% من {base}",
  "{percent}% of {base} from {date}": "{percent}% من {base} بداية من {date}",
  "{stars} ★ from {count} published reviews": "{stars} ★ من {count} تقييم منشور",
  "{start} to {end}": "من {start} إلى {end}",
  "{total} total · {recent} in the last 7 days": "{total} إجمالًا · {recent} في آخر 7 أيام",
  "{when} by {who}": "{when} بواسطة {who}",
  "Tournaments": "البطولات",
  "Venue tournaments waiting to be shown to players": "بطولات الأماكن المنتظرة قبل ما تظهر للاعبين",
  "Declined": "مرفوضة",
  "Decline": "رفض",
  "About": "عن البطولة",
  "How to enter": "طريقة الاشتراك",
  "Contact phone": "رقم التواصل",
  "Rules & entry terms": "القواعد وشروط الاشتراك",
  "the venue": "المكان",
  "No tournaments are waiting.": "مفيش بطولات منتظرة.",
  "No tournaments match this filter.": "مفيش بطولات بالفلتر ده.",
  "Tell the venue what to change before declining.": "قول للمكان إيه اللي محتاج يتغيّر قبل الرفض.",
  "{name} is now visible to players.": "{name} ظاهرة دلوقتي للاعبين.",
  "{name} was sent back to the venue.": "{name} رجعت للمكان عشان يعدّلها.",
  "Sent by {name} {date}": "أرسلها {name} {date}",
  "Reviewed by {name} {date}": "راجعها {name} {date}",
  "Declined before: {note}": "اترفضت قبل كده: {note}",
  "Note to the venue (needed to decline)": "ملاحظة للمكان (مطلوبة للرفض)",
  "Why take it down? The venue sees this.": "ليه هتشيلها؟ المكان هيشوف السبب ده.",
  // Bookings & sessions live activity (/admin/activity)
  "Bookings & sessions": "الحجوزات والجلسات",
  "Fixed time": "موعد ثابت",
  "Open time": "وقت مفتوح",
  "From booking": "من حجز",
  "Needs attention": "تحتاج متابعة",
  "Waiting for the venue to confirm": "منتظرة تأكيد المكان",
  "Start time passed with no check-in": "موعد البدء عدى من غير دخول",
  "Running past the planned end": "شغالة بعد النهاية المخططة",
  "Demo": "تجريبي",
  "Received {date}": "وصل {date}",
  "booking ref": "مرجع الحجز",
  "session ref": "مرجع الجلسة",
  "Copy {label}": "نسخ {label}",
  "Copied": "اتنسخ",
  "Place": "المكان",
  "Business contact": "تواصل النشاط",
  "Owners ({count})": "الملاك ({count})",
  "No owner contact on file": "مفيش بيانات تواصل للمالك",
  "No contact on file": "مفيش بيانات تواصل",
  "Times": "المواعيد",
  "Times shown in venue time.": "المواعيد بتوقيت المكان.",
  "Scheduled start (venue time)": "البداية المجدولة (بتوقيت المكان)",
  "Session start (venue time)": "بداية الجلسة (بتوقيت المكان)",
  "Scheduled end (venue time)": "النهاية المجدولة (بتوقيت المكان)",
  "Planned end (venue time)": "النهاية المخططة (بتوقيت المكان)",
  "Ended (venue time)": "انتهت (بتوقيت المكان)",
  "Created (when we received it)": "اتسجل (وقت استلامه)",
  "Last update": "آخر تحديث",
  "Linked booking": "الحجز المرتبط",
  "Open venue": "فتح المكان",
  "Open provider": "فتح الشريك",
  "Pause live updates": "إيقاف التحديث المباشر",
  "Resume live updates": "استئناف التحديث المباشر",
  "Loading live activity...": "جار تحميل النشاط المباشر...",
  "Could not load live activity.": "تعذر تحميل النشاط المباشر.",
  "Live activity could not be loaded. Check your connection and try again.": "تعذر تحميل النشاط المباشر. تحقق من اتصالك وحاول مرة أخرى.",
  "Your sign-in changed. Previous results were cleared. Sign in again to keep helping venues.": "تغيّرت جلسة الدخول. اتمسحت النتائج السابقة. سجّل الدخول تاني عشان تكمل مساعدة الأماكن.",
  "Your sign-in changed. Previous results were cleared.": "تغيّرت جلسة الدخول. اتمسحت النتائج السابقة.",
  "Stopped: sign-in changed. Previous results were cleared.": "توقف: تغيّرت جلسة الدخول. اتمسحت النتائج السابقة.",
  "Sign in again": "سجّل الدخول تاني",
  "Live updates paused. Showing {count} previous results.": "التحديث المباشر متوقف. عرض {count} من النتائج السابقة.",
  "Live updates paused.": "التحديث المباشر متوقف.",
  "Showing last good data from {date}. Refresh failed; will retry.": "عرض آخر بيانات سليمة من {date}. فشل التحديث؛ هنحاول تاني.",
  "earlier": "وقت سابق",
  "Last updated {date}. Refreshes every 15 seconds.": "آخر تحديث {date}. يتحدث كل 15 ثانية.",
  "Live. Refreshes every 15 seconds.": "مباشر. يتحدث كل 15 ثانية.",
  "Auto-refresh is paused. Press Refresh for the latest, or Resume to follow arrivals again.": "التحديث التلقائي متوقف. اضغط تحديث لآخر البيانات، أو استئناف لمتابعة الجديد.",
  "Updates pause while this tab is hidden. They resume when you come back.": "التحديث يقف لما التبويب ده يكون مخفي، ويرجع أول ما ترجع.",
  "All activity": "كل النشاط",
  "Any status": "أي حالة",
  "Needs attention only": "اللي محتاجة متابعة بس",
  "Venue: {id}": "المكان: {id}",
  "Clear venue filter": "مسح فلتر المكان",
  "Showing {from}–{to} of {total}": "عرض {from}–{to} من {total}",
  "{total} results": "{total} نتيجة",
  "No bookings or sessions match. History is included; try clearing search or filters.": "مفيش حجوزات أو جلسات مطابقة. السجل مشمول؛ جرّب تمسح البحث أو الفلاتر.",
  "No bookings or sessions match. Try clearing search or filters.": "مفيش حجوزات أو جلسات مطابقة. جرّب تمسح البحث أو الفلاتر.",
  "Activity pages": "صفحات النشاط",
  "Previous": "السابق",
  "Page {page} of {pages}": "صفحة {page} من {pages}",
  "Every booking and session as it arrives, newest first. Read-only. Call the venue if something needs attention.": "كل حجز وجلسة أول ما توصل، الأحدث الأول. للقراءة بس. كلّم المكان لو حاجة محتاجة متابعة.",
  "Booking or session": "حجز أو جلسة",
  "Search by player, venue, unit, phone or reference": "ابحث باللاعب أو المكان أو الوحدة أو الهاتف أو المرجع",
  "Player, venue, unit, phone or reference": "لاعب أو مكان أو وحدة أو هاتف أو مرجع",
  // Platform analytics (/admin/analytics)
  "Platform snapshot": "لقطة المنصة",
  "Active sessions at snapshot": "الجلسات النشطة وقت اللقطة",
  "Bookings on snapshot date": "حجوزات يوم اللقطة",
  "Loading snapshot...": "جار تحميل اللقطة...",
  "As of {date}. Cached for one hour; refresh after expiry.": "بتاريخ {date}. محفوظة لمدة ساعة؛ حدّث بعد انتهاء الصلاحية.",
  "Snapshot expired {date}. Refresh for the latest snapshot.": "انتهت صلاحية اللقطة {date}. حدّث للحصول على أحدث لقطة.",
  "Snapshot unavailable.": "اللقطة غير متاحة.",
  "Platform counts are a snapshot, not live. The date range applies to the reports below.": "أرقام المنصة لقطة محفوظة، مش أرقام مباشرة. الفترة المحددة تخص التقارير الموجودة بالأسفل.",
  "Accounts registered": "حسابات مسجلة",
  "Booked": "حجزوا",
  "Played": "لعبوا",
  "Counts accounts registered in this period, then how many booked, then how many played through a booking-linked session. Walk-ins without a booking are not counted.": "نعد الحسابات المسجلة في الفترة، ثم من حجز منها، ثم من لعب بجلسة مرتبطة بحجز. الزيارات المباشرة من غير حجز غير محسوبة.",
  "Full account history to {date}.": "كامل سجل الحسابات حتى {date}.",
  "Accounts registered {from} to {to}.": "حسابات مسجلة من {from} إلى {to}.",
  // Booking promotion campaigns (admin console, presentational Phase 1).
  "Enter the English offer name.": "أدخل اسم العرض بالإنجليزية.",
  "Enter the Arabic offer name.": "أدخل اسم العرض بالعربية.",
  "Enter the code players will type.": "أدخل الرمز الذي سيكتبه اللاعبون.",
  "Enter a percent above zero, for example 10 or 12.5.": "أدخل نسبة أكبر من الصفر، مثل 10 أو 12.5.",
  "Enter a fixed amount in EGP above zero.": "أدخل مبلغًا ثابتًا بالجنيه أكبر من الصفر.",
  "Enter a cap in EGP above zero, or leave it empty.": "أدخل حدًا أقصى بالجنيه أكبر من الصفر، أو اتركه فارغًا.",
  "Choose when the offer starts.": "اختر موعد بدء العرض.",
  "The end must be after the start.": "يجب أن تكون النهاية بعد البداية.",
  "Use 24-hour HH:mm.": "استخدم صيغة الـ24 ساعة HH:mm.",
  "Enter a budget in EGP above zero, or leave it empty for no budget limit.": "أدخل ميزانية بالجنيه أكبر من الصفر، أو اتركها فارغة بلا حد للميزانية.",
  "Enter a whole number above zero, or leave it empty for unlimited uses.": "أدخل عددًا صحيحًا أكبر من الصفر، أو اتركه فارغًا لاستخدامات بلا حد.",
  "Enter a whole number above zero, or leave it empty for no per-customer limit.": "أدخل عددًا صحيحًا أكبر من الصفر، أو اتركه فارغًا بلا حد لكل عميل.",
  "You are revising revision {revision}. New terms apply to future bookings. Accepted holds keep the terms they were given.": "أنت تراجع المراجعة {revision}. الشروط الجديدة تسري على الحجوزات القادمة. الحجوزات المقبولة تحتفظ بالشروط التي قُدمت بها.",
  "Support accounts can read campaigns. Only admins can create or change them.": "حسابات الدعم يمكنها قراءة الحملات. المديرون فقط يمكنهم إنشاؤها أو تغييرها.",
  "Players see the offer name next to the original charge and the savings.": "يرى اللاعبون اسم العرض بجانب المبلغ الأصلي والتوفير.",
  "Offer": "العرض",
  "Name (English)": "الاسم (بالإنجليزية)",
  "Name (Arabic)": "الاسم (بالعربية)",
  "How players get it": "كيف يحصل عليه اللاعبون",
  "Automatic": "تلقائي",
  "Code": "رمز",
  "Saved as uppercase. Automatic offers have no code.": "يُحفظ بأحرف كبيرة. العروض التلقائية بلا رمز.",
  "One offer applies per booking. The server checks that the savings fit the campaign funding.": "يسري عرض واحد على كل حجز. يتحقق الخادم من أن التوفير يناسب تمويل الحملة.",
  "Savings": "التوفير",
  "Discount type": "نوع الخصم",
  "Fixed amount (EGP)": "مبلغ ثابت (بالجنيه)",
  "Fixed amount": "مبلغ ثابت",
  "Maximum saving per booking (EGP, optional)": "أقصى توفير لكل حجز (بالجنيه، اختياري)",
  "No cap": "بلا حد أقصى",
  "The window uses the booked start in the venue's timezone. Quotes use the booked start as well.": "تعتمد الفترة على بداية الحجز بتوقيت المكان. عروض الأسعار تعتمد على بداية الحجز أيضًا.",
  "Schedule": "المواعيد",
  "Ends at (optional)": "ينتهي في (اختياري)",
  "Empty means the offer stays open until paused or exhausted.": "تركه فارغًا يعني بقاء العرض مفتوحًا حتى الإيقاف أو النفاد.",
  "Days (optional)": "الأيام (اختياري)",
  "Empty means every day.": "تركه فارغًا يعني كل الأيام.",
  "Daily from (optional)": "يوميًا من (اختياري)",
  "Daily until (optional)": "يوميًا حتى (اختياري)",
  "Empty selections mean everywhere and everything.": "الاختيارات الفارغة تعني كل الأماكن وكل شيء.",
  "Venues (optional)": "الأماكن (اختياري)",
  "No venues to choose from yet. Saving without a selection applies everywhere.": "لا توجد أماكن للاختيار بعد. الحفظ بلا اختيار يسري في كل مكان.",
  "Unit types (optional)": "أنواع الوحدات (اختياري)",
  "Budgets and use limits count across all revisions. Lowering them never changes already accepted bookings.": "تُحتسب الميزانيات وحدود الاستخدام عبر كل مراجعات الحملة. خفضها لا يغيّر الحجوزات المقبولة.",
  "Budget (EGP, optional)": "الميزانية (بالجنيه، اختياري)",
  "Limits include held bookings, not only completed ones.": "تشمل الحدود الحجوزات المحتجزة، وليست المكتملة فقط.",
  "No limit": "بلا حد",
  "Total uses (optional)": "إجمالي الاستخدامات (اختياري)",
  "Uses per customer (optional)": "استخدامات كل عميل (اختياري)",
  "First booking only": "الحجز الأول فقط",
  "First means the first completed, fully paid booking. An accepted hold keeps its eligibility.": "الأول يعني أول حجز مكتمل ومدفوع بالكامل. الحجز المحتجَز المقبول يحتفظ بأهليته.",
  "New campaign": "حملة جديدة",
  "Budgets and use limits count across all revisions of a campaign.": "تُحتسب الميزانيات وحدود الاستخدام عبر كل مراجعات الحملة.",
  "Booking campaigns": "حملات الحجوزات",
  "No campaigns yet.": "لا توجد حملات بعد.",
  "Campaign": "الحملة",
  "State": "الحالة",
  "Used": "مستخدَم",
  "Budget left": "المتبقي من الميزانية",
  "Actions": "إجراءات",
  "{used} used": "مستخدَم {used}",
  "{used} used · {left} left": "مستخدَم {used} · المتبقي {left}",
  "Pause": "إيقاف",
  "Resume": "استئناف",
  "Unknown": "غير معروف",
  "Revise terms": "مراجعة الشروط",
  "New terms apply to future bookings. Accepted holds keep the terms they were given.": "الشروط الجديدة تسري على الحجوزات القادمة. الحجوزات المحتجَزة المقبولة تحتفظ بالشروط التي قُدمت بها.",
  "Arabic name": "الاسم بالعربية",
  "Code: {code}": "الرمز: {code}",
  "Maximum saving per booking": "أقصى توفير لكل حجز",
  "Revision": "المراجعة",
  "The window uses the booked start in the venue's timezone.": "تعتمد الفترة على بداية الحجز بتوقيت المكان.",
  "Schedule and scope": "المواعيد والنطاق",
  "No end": "بلا نهاية",
  "Days": "الأيام",
  "Every day": "كل الأيام",
  "Daily window": "الفترة اليومية",
  "All day": "طوال اليوم",
  "Everywhere": "في كل مكان",
  "Unit types": "أنواع الوحدات",
  "Everything": "كل شيء",
  "Limits count across all revisions. Lowering them never changes already accepted bookings.": "تُحتسب الحدود عبر كل المراجعات. خفضها لا يغيّر الحجوزات المقبولة.",
  "Budget and uses": "الميزانية والاستخدامات",
  "Uses left": "الاستخدامات المتبقية",
  "Uses per customer": "استخدامات كل عميل",
  "Reserved (held) uses": "استخدامات محتجَزة",
  "Consumed (completed, paid) uses": "استخدامات مستهلَكة (مكتملة ومدفوعة)",
  "Released uses": "استخدامات مُحررة",
  "Reserved credit": "رصيد محتجَز",
  "Consumed credit": "رصيد مستهلَك",
  "Released credit": "رصيد مُحرر",
  "Refunded credit": "رصيد مُسترد",
  "Reserved credit is quoted and held. Consumed credit is actual completed and paid savings. Refunded credit reversed part of it later.": "الرصيد المحتجَز مسعّر ومحتجَز. الرصيد المستهلَك توفير فعلي لحجوزات مكتملة ومدفوعة. الرصيد المُسترد عكس جزءًا منه لاحقًا.",
  "Booking IDs only. Customer contact details are never shown here.": "معرّفات الحجوزات فقط. بيانات تواصل العملاء لا تظهر هنا أبدًا.",
  "Redemptions": "الاستخدامات",
  "No redemptions recorded for this campaign.": "لا توجد استخدامات مسجلة لهذه الحملة.",
  "Booked at": "موعد الحجز",
  // Booking promotion campaigns (admin console, Phase 2 integration).
  "Released": "مُحرر",
  "Consumed": "مستهلَك",
  "Final credit": "الرصيد النهائي",
  "Gross commission (before booking savings)": "إجمالي العمولة قبل توفير الحجز",
  "Before booking savings; reserved or finalized amount, not current earnings.": "قبل توفير الحجز؛ مبلغ تقديري محتجَز أو نهائي مثبّت، وليس الأرباح الحالية.",
  "Reserved estimate": "تقدير محتجَز",
  "Final": "نهائي",
  "Targeting and limits (optional)": "الاستهداف والحدود (اختياري)",
  "Applies everywhere, every day, with no limits.": "يسري في كل مكان وكل يوم وبلا حدود.",
  "Reserved at": "موعد الاحتجاز",
  "Load more": "عرض المزيد",
  "Redemptions could not be loaded.": "تعذر تحميل الاستخدامات.",
  "More redemptions could not be loaded.": "تعذر تحميل المزيد من الاستخدامات.",
  "Discount campaigns for bookings. Pausing affects new bookings only.": "حملات خصم للحجوزات. الإيقاف يؤثر على الحجوزات الجديدة فقط.",
  "New terms apply from the start moment. Set a code only for code offers.": "تسري الشروط الجديدة من لحظة البدء. أدخل رمزًا لعروض الرموز فقط.",
  "Revisions never rewrite history. Accepted holds keep the terms they were given.": "المراجعات لا تعيد كتابة الماضي. الحجوزات المحتجَزة المقبولة تحتفظ بالشروط التي قُدمت بها.",
  "Save new revision": "حفظ مراجعة جديدة",
  "Create campaign": "إنشاء الحملة",
  "The campaign was not created.": "لم تُنشأ الحملة.",
  "The campaign was not revised.": "لم تُراجع الحملة.",
  "The campaign was not paused.": "لم تُوقف الحملة.",
  "The campaign was not resumed.": "لم تُستأنف الحملة.",
  "Campaign paused. It no longer applies to new bookings.": "تم إيقاف الحملة. لم تعد تسري على الحجوزات الجديدة.",
  "Campaign resumed. New bookings can receive it again.": "تم استئناف الحملة. يمكن للحجوزات الجديدة الاستفادة منها مجددًا.",
  "Pause this campaign?": "إيقاف هذه الحملة؟",
  "Resume this campaign?": "استئناف هذه الحملة؟",
  "New bookings stop receiving it. Accepted holds keep the terms they were given.": "تتوقف الحجوزات الجديدة عن الاستفادة منها. الحجوزات المحتجَزة المقبولة تحتفظ بالشروط التي قُدمت بها.",
  "New bookings can receive it again. Past bookings are unchanged.": "يمكن للحجوزات الجديدة الاستفادة منها مجددًا. الحجوزات السابقة لا تتغير.",
  "This campaign changed while you were editing (now revision {revision}). Your entries are kept below. Review the latest terms, then submit again to confirm.": "تغيّرت هذه الحملة أثناء تحريرك (أصبحت المراجعة {revision}). مدخلاتك محفوظة بالأسفل. راجع أحدث الشروط، ثم أرسل مجددًا للتأكيد.",
  "Venues could not be loaded. You can still save; an empty selection applies everywhere.": "تعذر تحميل الأماكن. يمكنك الحفظ؛ الاختيار الفارغ يسري في كل مكان.",
  "Start and end are exact moments saved as UTC. Enter them in your browser time; days and hours match each booking in its own venue time.": "البداية والنهاية لحظتان محددتان تُحفظان بتوقيت UTC. أدخلهما بتوقيت متصفحك؛ الأيام والساعات تُطابق كل حجز بتوقيت مكانه.",
  "Your browser time is {zone}.": "توقيت متصفحك هو {zone}.",
  "Empty selections mean everywhere and everything. Days and hours always follow each booking's own venue time, even when several venues are selected.": "الاختيارات الفارغة تعني كل الأماكن وكل شيء. الأيام والساعات تتبع دائمًا توقيت مكان كل حجز، حتى عند اختيار عدة أماكن.",
  "Enter both the daily start and end, or leave both empty.": "أدخل بدايتي اليوم ونهايته معًا، أو اتركهما فارغين معًا.",
  "Daily start and end must differ.": "يجب أن تختلف بداية اليوم عن نهايته.",
};

export type TranslationKey = keyof typeof en;

export const languageOptions: Array<{ labelKey: TranslationKey; value: AppLanguage }> = [
  { value: "ar", labelKey: "preferences.language.ar" },
  { value: "en", labelKey: "preferences.language.en" },
];

export const themeOptions: Array<{ labelKey: TranslationKey; value: ThemePreference }> = [
  { value: "dark", labelKey: "preferences.theme.dark" },
  { value: "light", labelKey: "preferences.theme.light" },
];

interface AppPreferencesContextValue {
  direction: "ltr" | "rtl";
  language: AppLanguage;
  resolvedLanguage: ResolvedLanguage;
  resolvedTheme: ResolvedTheme;
  setLanguage: (language: AppLanguage) => void;
  setThemePreference: (theme: ThemePreference) => void;
  t: TranslationFunction;
  themePreference: ThemePreference;
}

const AppPreferencesContext = createContext<AppPreferencesContextValue | null>(null);
const languageStorageKey = "gaamed.web.language";
const themeStorageKey = "gaamed.web.theme";

function getStoredPreference(storageKey: string) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function getBrowserLanguagePreference(): AppLanguage {
  if (typeof navigator === "undefined") {
    return "en";
  }

  const browserLanguage = (navigator.languages?.[0] ?? navigator.language ?? "").toLowerCase();
  return browserLanguage.startsWith("ar") ? "ar" : "en";
}

function getBrowserThemePreference(): ThemePreference {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "dark";
  }

  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function interpolate(template: string, values?: TranslationValues) {
  if (!values) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key];
    return value === null || value === undefined ? "" : String(value);
  });
}

export function AppPreferencesProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    const stored = getStoredPreference(languageStorageKey);
    if (stored === "en" || stored === "ar") {
      return stored;
    }

    return getBrowserLanguagePreference();
  });
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    const stored = getStoredPreference(themeStorageKey);
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    return getBrowserThemePreference();
  });

  const resolvedLanguage = language;
  const resolvedTheme = themePreference;
  const direction: "ltr" | "rtl" = resolvedLanguage === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.lang = resolvedLanguage;
    document.documentElement.dir = direction;
  }, [direction, resolvedLanguage, resolvedTheme]);

  const setLanguage = useCallback((nextLanguage: AppLanguage) => {
    setLanguageState(nextLanguage);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(languageStorageKey, nextLanguage);
    }
  }, []);

  const setThemePreference = useCallback((nextTheme: ThemePreference) => {
    setThemePreferenceState(nextTheme);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(themeStorageKey, nextTheme);
    }
  }, []);

  const t = useCallback<TranslationFunction>(
    (key, values) => {
      const english = (en as Record<string, string>)[key] ?? key;
      const template = resolvedLanguage === "ar" ? ar[key] ?? english : english;
      return interpolate(template, values);
    },
    [resolvedLanguage],
  );

  const value = useMemo(
    () => ({
      direction,
      language,
      resolvedLanguage,
      resolvedTheme,
      setLanguage,
      setThemePreference,
      t,
      themePreference,
    }),
    [direction, language, resolvedLanguage, resolvedTheme, setLanguage, setThemePreference, t, themePreference],
  );

  return <AppPreferencesContext.Provider value={value}>{children}</AppPreferencesContext.Provider>;
}

export function useAppPreferences() {
  const value = useContext(AppPreferencesContext);

  if (!value) {
    throw new Error("useAppPreferences must be used within AppPreferencesProvider");
  }

  return value;
}
